import { ValidationResponse } from "../types/validationTypes";
import { getVendorById } from "./vendorApi";
import { getCustomerById } from "./customerApi";
import { API_BASE_URL, CORE_API_URL } from "../config/api";

// Bank Account Types
export interface BankAccount {
  id: string;
  bank_name: string;
  zoho_bankaccountid: string;
  currency_id: string;
  account_type: string;
  is_active: boolean;
  is_auto: boolean;
  balance: number;
  bank_balance: number;
  is_primary_account: boolean;
  statement_suggestion_count: number;
  is_paypal_account: boolean;
  currency_code: string;
  created_at: string;
  updated_at: string;
}

export interface BankAccountsStatistics {
  total_accounts: number;
  active_accounts: number;
  primary_accounts: number;
  paypal_accounts: number;
  bank_accounts: number;
  cash_accounts: number;
}

export interface BankAccountsResponse {
  success: boolean;
  message: string;
  data: {
    bank_accounts: BankAccount[];
    total_count: number;
    statistics: BankAccountsStatistics;
  };
}

// Transaction Types
export interface Transaction {
  id?: number;
  transaction_id: string;
  date: string;
  description: string;
  amount: number;
  user_status: string;
  currency_code?: string;
  currency_symbol?: string;
  zoho_partyid?: string;
  zoho_partyid_confidence?: string;
  zoho_partyid_source?: string;
  zoho_party_id_confidence?: string;
  zoho_partytype?: string;
  zoho_category?: string;
  zoho_gl?: string;
  updated_at?: string;
  category_type?: string;
  review_status?: "pending" | "approved" | "rejected" | null;
}

export interface TransactionResponse {
  success: boolean;
  data: Transaction[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    current_page: number;
    total_pages: number;
    has_more: boolean;
  };
}

export type TransactionCategoryType =
  | "customer_payment"
  | "transfer_from_another_account"
  | "interest_income"
  | "other_income"
  | "deposit"
  | "deposit_split"
  | "owners_contribution"
  | "vendor_payment_refund"
  | "credits_or_refund_received"
  | "vendor_payment"
  | "vendor_expense"
  | "customer_expense_refund"
  | "transfer_to_another_account"
  | "transfer_to_other_accounts"
  | "transfer_to_other_accounts_split"
  | "owners_drawings"
  | "customer_payment_refund"
  | "card_payment";

export interface BankTransactionData {
  id: number;
  transaction_id: string;
  date: string;
  amount: number;
  description: string;
  user_status: string;
  status: string;
  debit_or_credit?: "credit" | "debit";
  category_type: TransactionCategoryType | null;
  transaction_match_json?: string | null;
  from_account_id?: string | null;
  to_account_id?: string | null;
  customer_id?: string | null;
  vendor_id?: string | null;
  zoho_bankaccountid?: string;
  account_name?: string;
  [key: string]: any;
}

export interface CustomerPaymentInvoiceDetail {
  invoice_id: string;
  invoice_number: string;
  total: number;
  balance: number;
  amount_applied: number;
  adjustment_amount?: number;
  currency_symbol?: string;
  tds_section?: string;
  tds_tax_amount?: number;
  tds_tax_id?: string;
  tds_tax_percentage?: number;
  tds_tax_name?: string;
}

export interface CustomerPaymentCategoryDetails {
  customer_id: string;
  customer_name: string;
  advance_payment: number;
  invoices: CustomerPaymentInvoiceDetail[];
  exchange_rate?: number | null;
  bank_charges?: number | null;
}

export interface TransferFromAccountCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  to_account_id?: string | null;
  to_account_name: string;
  date: string;
  reference_number?: string;
  from_transaction_id?: string | null;
  to_transaction_id?: string | null;
}

export interface InterestIncomeCategoryDetails {
  from_account_id?: string | null;
  from_account_name?: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface OtherIncomeCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface DepositFromOtherAccountsCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  customer_id?: string | null;
  customer_name?: string | null;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface DepositFromOtherAccountsSplitCategoryDetails {
  date?: string;
  reference_number?: string;
  customer_id?: string | null;
  customer_name?: string | null;
  splits?: Array<{
    from_account_id: string;
    from_account_name: string;
    amount: number;
    payment_mode: string;
  }>;
  total_amount?: number;
  split_count?: number;
}

export interface SplitCustomerCategoryDetails {
  date?: string;
  reference_number?: string;
  split_count?: number;
  total_amount?: number;
  splits: Array<{
    customer_id?: string | null;
    customer_name: string;
    amount: number;
    invoices: Array<{
      invoice_id?: string;
      invoice_number?: string;
      total?: number;
      balance?: number;
      amount_applied?: number;
      adjustment_amount?: number;
      currency_symbol?: string;
      tds_tax_amount?: number;
      tds_tax_name?: string;
      tds_tax_percentage?: number;
      tds_section?: string;
    }>;
    advance_payment: number;
  }>;
}

export interface SplitVendorCategoryDetails {
  date?: string;
  reference_number?: string;
  split_count?: number;
  total_amount?: number;
  splits: Array<{
    vendor_id?: string | null;
    vendor_name: string;
    amount: number;
    bills: Array<{
      bill_id?: string;
      bill_number?: string;
      total?: number;
      balance?: number;
      amount_applied?: number;
      adjustment_amount?: number;
      currency_symbol?: string;
    }>;
    advance_payment: number;
  }>;
}

export interface OwnersContributionCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface VendorPaymentRefundPaymentDetail {
  payment_id: string;
  payment_number?: string;
  amount: number;
  balance: number;
  amount_refunded: number;
}

export interface VendorPaymentRefundCategoryDetails {
  vendor_id?: string | null;
  vendor_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
  to_account_id?: string | null;
  to_account_name?: string;
  payment: VendorPaymentRefundPaymentDetail | null;
}

export interface CreditsOrRefundReceivedCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
  vendor_id?: string | null;
  vendor_name?: string | null;
}

export interface VendorPaymentBillDetail {
  bill_id: string;
  bill_number: string;
  total?: number | null;
  balance?: number | null;
  currency_code?: string;
  currency_symbol?: string;
  amount_applied: number;
  adjustment_amount?: number;
}

export interface VendorPaymentCategoryDetails {
  vendor_id?: string | null;
  vendor_name: string;
  date: string;
  paid_through_account_id?: string | null;
  paid_through_account_name: string;
  bills: VendorPaymentBillDetail[];
  advance_payment?: number;
  amount: number;
  currency_code?: string;
  currency_symbol?: string;
  exchange_rate?: number | null;
  bank_charges?: number | null;
}

export interface VendorTransferToAccountCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  to_account_id?: string | null;
  to_account_name: string;
  date: string;
  reference_number?: string;
  amount: number;
}

export interface VendorExpenseCategoryDetails {
  account_id: string;
  gl_name?: string | null;
  paid_through_account_id?: string | null;
  paid_through_account_name?: string | null;
  date: string;
  amount: number;
  notes?: string | null;
  currency_code?: string;
  currency_symbol?: string;
}

export interface CustomerExpenseRefundCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  amount: number;
  date: string;
}

export interface VendorOwnerDrawingCategoryDetails {
  // from_account_id?: string | null;
  // from_account_name: string;
  to_account_id?: string | null;
  to_account_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
  amount: number;
}

export interface VendorCustomerPaymentRefundPaymentDetail {
  payment_id: string;
  payment_number?: string;
  amount: number;
  balance: number;
  amount_refunded: number;
}

export interface VendorCustomerPaymentRefundCategoryDetails {
  customer_id?: string | null;
  customer_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
  from_account_id?: string | null;
  from_account_name: string;
  payment: VendorCustomerPaymentRefundPaymentDetail | null;
}

export interface VendorCardPaymentCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  to_account_id?: string | null;
  to_account_name: string;
  date: string;
  reference_number?: string;
  amount: number;
}

export interface VendorTransferToOtherAccountsCategoryDetails {
  from_account_id?: string | null;
  from_account_name: string;
  to_account_id?: string | null;
  to_account_name: string;
  date: string;
  payment_mode: string;
  reference_number?: string;
  amount: number;
}

export type CustomerTransactionViewDetailsData =
  | {
      transaction_data: BankTransactionData;
      category_type: "customer_payment";
      category_details: CustomerPaymentCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "transfer_from_another_account";
      category_details: TransferFromAccountCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "interest_income";
      category_details: InterestIncomeCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "other_income";
      category_details: OtherIncomeCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "deposit";
      category_details: DepositFromOtherAccountsCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "deposit_split";
      category_details: DepositFromOtherAccountsSplitCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "split_customer";
      category_details: SplitCustomerCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "split_vendor";
      category_details: SplitVendorCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "owners_contribution";
      category_details: OwnersContributionCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "vendor_payment_refund";
      category_details: VendorPaymentRefundCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "credits_or_refund_received";
      category_details: CreditsOrRefundReceivedCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "vendor_payment";
      category_details: VendorPaymentCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "transfer_to_another_account";
      category_details: VendorTransferToAccountCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "owners_drawings";
      category_details: VendorOwnerDrawingCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "customer_payment_refund";
      category_details: VendorCustomerPaymentRefundCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "card_payment";
      category_details: VendorCardPaymentCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "vendor_expense";
      category_details: VendorExpenseCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "customer_expense_refund";
      category_details: CustomerExpenseRefundCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "transfer_to_other_accounts";
      category_details: VendorTransferToOtherAccountsCategoryDetails;
    }
  | {
      transaction_data: BankTransactionData;
      category_type: "transfer_to_other_accounts_split";
      category_details: VendorTransferToOtherAccountsSplitCategoryDetails;
    };

export interface TransactionActivityLog {
  id: number;
  activity_name: string;
  action_type: string;
  user_name: string;
  user_email: string;
  description: string;
  details: Record<string, any> | null;
  created_at: string;
}

export interface TransactionActivityLogsResponse {
  success: boolean;
  message?: string;
  data: {
    logs: TransactionActivityLog[];
    count: number;
  };
}

export interface CustomerTransactionViewDetailsResponse {
  success: boolean;
  data: CustomerTransactionViewDetailsData;
}

// API Response Types
const API_STATUS = {
  SUCCESS: "success",
  ERROR: "error",
};

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// Get stored token
const getStoredToken = () => {
  try {
    return localStorage.getItem("access_token");
  } catch (error) {
    console.error("Error getting stored token:", error);
    return null;
  }
};

// Store token securely
const storeToken = (token: string) => {
  try {
    localStorage.setItem("access_token", token);
  } catch (error) {
    console.error("Error storing token:", error);
    throw new Error("Failed to store authentication token");
  }
};

// Remove stored token
const removeToken = () => {
  try {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_data");
  } catch (error) {
    console.error("Error removing token:", error);
  }
};

// Get stored user data
const getStoredUserData = () => {
  try {
    const userData = localStorage.getItem("user_data");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting stored user data:", error);
    return null;
  }
};

// Get stored company data
const getStoredCompanyData = () => {
  try {
    const companyData = localStorage.getItem("company_data");
    return companyData ? JSON.parse(companyData) : null;
  } catch (error) {
    console.error("Error getting stored company data:", error);
    return null;
  }
};

// Store user data
const storeUserData = (userData: any) => {
  try {
    localStorage.setItem("user_data", JSON.stringify(userData));
  } catch (error) {
    console.error("Error storing user data:", error);
    throw new Error("Failed to store user data");
  }
};

const storeCompanyData = (companyData: any) => {
  try {
    localStorage.setItem("company_data", JSON.stringify(companyData));
  } catch (error) {
    console.error("Error storing user data:", error);
    throw new Error("Failed to store user data");
  }
};

// Store refresh token
const storeRefreshToken = (refreshToken: string) => {
  try {
    localStorage.setItem("refresh_token", refreshToken);
  } catch (error) {
    console.error("Error storing refresh token:", error);
    throw new Error("Failed to store refresh token");
  }
};

// Create headers for API requests
const createHeaders = (includeAuth = true): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (includeAuth) {
    const token = getStoredToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Make API request
const makeRequest = async (endpoint: string, options: any = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  // const url = `${endpoint}`;
  const config = {
    headers: createHeaders(options.includeAuth !== false),
    ...options,
  };

  try {
    console.log(`Making API request to: ${url}`, config);

    const response = await fetch(url, config);
    const data = await response.json();

    console.log(`API Response for ${endpoint}:`, data);

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    // Network or other errors
    console.error(`API request failed for ${endpoint}:`, error);
    const err = error as any;
    throw new APIError(
      err.message || "Network error occurred",
      err.status || 0,
      { originalError: error },
    );
  }
};

// Authentication API calls
export const authAPI = {
  // Login (works for both regular users and admins)
  login: async (credentials: any) => {
    const response = await makeRequest("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
      includeAuth: false,
    });

    if (response.success && response.data) {
      const { user, token, company } = response.data;

      // Store token and user data
      storeToken(token);
      storeUserData(user);
      storeCompanyData(company);

      return {
        success: true,
        data: {
          user,
          company,
          token,
        },
      };
    }

    throw new APIError("Login failed", HTTP_STATUS.BAD_REQUEST, response);
  },

  // Admin login (checks for admin role)
  adminLogin: async (credentials: any) => {
    console.log("Admin login API call with credentials:", credentials);
    const response = await makeRequest("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
      includeAuth: false,
    });
    console.log("Admin login API response:", response);

    if (response.success && response.data) {
      const { user, token, company } = response.data;

      // Check if user is admin (case-insensitive)
      if (user.role?.toLowerCase() !== "admin") {
        throw new APIError(
          "Access denied. Admin privileges required.",
          HTTP_STATUS.FORBIDDEN,
        );
      }

      // Store token and user data
      storeToken(token);
      storeUserData(user);

      return {
        success: true,
        data: {
          user,
          company,
          token,
        },
      };
    }

    throw new APIError("Admin login failed", HTTP_STATUS.BAD_REQUEST, response);
  },

  // Logout
  logout: async () => {
    try {
      // Call logout endpoint if it exists
      await makeRequest("/api/auth/logout", {
        method: "POST",
      });
    } catch (error) {
      console.warn("Logout API call failed:", error);
      // Continue with local logout even if API call fails
    } finally {
      // Always clear local storage
      removeToken();
    }
  },

  // Update user Zoho token
  updateUserZohoToken: async (zohoRefreshToken: string) => {
    const response = await makeRequest("/api/auth/updateUser", {
      method: "PUT",
      body: JSON.stringify({
        zoho_refresh_token: zohoRefreshToken,
      }),
    });

    if (response.success) {
      return {
        success: true,
        message: response.message,
        data: response.data,
      };
    }

    throw new APIError(
      "Failed to update Zoho token",
      HTTP_STATUS.BAD_REQUEST,
      response,
    );
  },
};

// Users API calls
export const usersAPI = {
  getUsers: async () => {
    console.log("Fetching users...");
    const response: any = await makeRequest("/api/users", {
      method: "GET",
    });

    console.log("Users fetched:", response);

    if (response.success && Array.isArray(response.data)) {
      return response.data;
    }

    throw new APIError(
      "Failed to fetch users",
      HTTP_STATUS.BAD_REQUEST,
      response,
    );
  },
  getUsersPaginated: async (
    search: string = "",
    limit: number = 10,
    offset: number = 0,
  ) => {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    const response = await makeRequest(`/api/users?${params.toString()}`, {
      method: "GET",
    });

    if (response.success && Array.isArray(response.data)) {
      return {
        items: response.data,
        hasMore: response.hasMore ?? false,
      };
    }

    throw new APIError(
      "Failed to fetch users",
      HTTP_STATUS.BAD_REQUEST,
      response,
    );
  },
};

// Query Counts Interface
export interface QueryCounts {
  success: boolean;
  data: {
    user_id: number;
    user_name: string;
    raised_by_me: {
      total: number;
      by_status: {
        open: number;
        in_progress: number;
        resolved: number;
        closed: number;
      };
    };
    assigned_to_me: {
      total: number;
      by_status: {
        open: number;
        in_progress: number;
        resolved: number;
        closed: number;
      };
    };
    total_queries_in_system: number;
  };
  message: string;
}

// Queries API - DEPRECATED: Use clean architecture from frontend/src/shared/queries/
// All query functionality now uses hooks → use cases → repository pattern
// The old queriesAPI export has been removed. Use the new hooks from:
// - useQueries() for listing queries
// - useQueryDetails() for query details
// - useCreateQuery() for creating queries
// - useResolveQuery() for resolving queries
// - useAddComment() for adding comments
// See: frontend/src/shared/queries/presentation/hooks/

// Data upload API calls
export const dataAPI = {
  upload: async (formData: FormData) => {
    const url = `${CORE_API_URL}/invoice_processing_2_0.php?api=upload`;
    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });
      console.log(response);
      return response.json();
    } catch (error) {
      console.error("Upload request failed:", error);
      throw error;
    }
  },
};

// Bills API - Enhanced Interfaces
export type BillStatus = "0" | "1" | "2" | "3" | "4" | "10";

export type BillReviewStatus = "0" | "1" | "2";

export type GstTreatment =
  | "business_gst"
  | "consumer"
  | "overseas"
  | "unregistered"
  | "sez";

export type CurrencyCode = "INR" | "USD" | "EUR" | "GBP";

export type Unit = "kgs" | "nos" | "pcs" | "litres" | "meters" | "sqft";

// Enhanced LineItem interface with additional fields from your JSON
export interface LineItem {
  name: string;
  description: string;
  sku: string;
  hsn_or_sac: string;
  hsn_description: string;
  quantity: number;
  rate: number;
  item_total: number;
  item_order: number;
  unit: Unit;
  account_id: string;
  original_account_id?: string;
  account_name: string;
  tax_id: string;
  tax_name: string;
  tax_percentage: number;
  tax_amount: number;
  item_total_inclusive_of_tax: number;
  is_billable: boolean;

  // Additional fields from your JSON data
  gl_recommendation?: string;
  gl_confidence?: number;
  gl_source_layer?: string;

  // TDS fields
  tds_tax_id?: string | null;
  tds_tax_percentage?: number;
  tds_tax_name?: string | null;
}

// Enhanced BillData interface
export interface BillData {
  /**
   * ZOUR-65 — the pipeline's location match for this document
   * (`{confidence, reason, input_gstin, location_id, name, is_active}`).
   * Absent on a document the pipeline has not touched; core recomputes it then.
   */
  location_match?: Record<string, unknown> | null;
  /**
   * ZOUR-65 — the Zoho location (GST registration) this document is filed
   * against. null/absent means the primary location.
   */
  location_id?: string | null;
  vendor_id: string;
  bill_number: string;
  date: string; // ISO date string
  due_date: string; // ISO date string
  reference_number: string;
  currency_id: string;
  currency_code: CurrencyCode;
  currency_symbol: string;
  exchange_rate: number;
  vendor_name: string;
  vendor_gstin: string;
  discount?: number;
  gst_no: string;
  gst_treatment: GstTreatment;
  customer_name: string;
  customer_gstin: string;
  source_of_supply: string;
  destination_of_supply: string;
  place_of_supply: string;
  invoice_number: string;
  invoice_date: string; // ISO date string
  eway_bill_number: string;
  irn_number: string;
  line_items: LineItem[];
  sub_total: number;
  tax_total: number;
  total: number;
  payment_terms: number;
  payment_terms_label: string;
  is_item_level_tax_calc: boolean;
  is_inclusive_tax: boolean;
  notes: string;
  terms: string;
  // Additional fields that might be present
  processing_metadata?: {
    extracted_at: string;
    confidence_score: number;
    extraction_method: string;
    version: string;
  };
  validation_results?: {
    is_valid: boolean;
    errors: string[];
    warnings: string[];
  };
  vendor_address?: string;
  // TDS fields
  tds_applicable?: string;
  tds_tax_id?: string | null;
  tds_tax_percentage?: number;
  tds_tax_name?: string | null;
}

export interface Bill {
  // Core Identifiers
  id: string;
  jobid: string;
  companyid: string;

  // File Information
  original_filename: string;

  // Processed Data
  bill_json: BillData; // Can be typed as BillData from documentTypes.ts if needed
  // bill_candidates: BillCandidate[] | null;

  // Status Information
  reviewed: BillReviewStatus;
  status: BillStatus;

  // External Integration
  zoho_billid: string | null;
  posted_to_zoho_date: string | null;
  // System Fields
  deleted: "0" | "1" | boolean;
  created_at: string;
  updated_at: string;

  // File Access
  signed_url: string;
  signed_url_expires_at: string;

  // Manual documents: attachments with signed URLs
  attachment_urls?: Array<{
    signed_url: string;
    filename: string;
    file_size?: number | null;
    expires_in?: number;
  }>;

  gst_type: string;

  // Optional Processing Information
  processing_notes?: string;
  error_message?: string;
  extracted_data_confidence?: number;
  processing_duration?: number;
  retry_count?: number;
}

export interface BillsPagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  current_page?: number;
  total_pages?: number;
}

export interface BillsResponse {
  success: boolean;
  data: {
    bills: Bill[];
    pagination: BillsPagination;
  };
  message?: string;
}

export interface BillsErrorResponse {
  success: false;
  error: string;
  message?: string;
  details?: {
    field?: string;
    code?: string;
    value?: any;
  };
}

export type BillsApiResponse = BillsResponse | BillsErrorResponse;

export interface BillsParams {
  // Pagination
  limit?: number;
  offset?: number;

  // Filtering
  status?: BillStatus;
  reviewed?: BillReviewStatus;
  search?: string;
  companyid?: string;

  // Date Filtering
  date_from?: string;
  date_to?: string;
  created_after?: string;
  created_before?: string;

  // Sorting
  sort_by?:
    | "created_at"
    | "updated_at"
    | "filename"
    | "status"
    | "original_filename";
  sort_order?: "asc" | "desc";

  // Additional Filters
  has_zoho_billid?: boolean;
  has_errors?: boolean;
  min_confidence?: number;
  max_confidence?: number;
}

export interface CreateBillRequest {
  filename: string;
  original_filename: string;
  file_size: string;
  folder_id: string;
  companyid: string;
  bill_json?: any;
}

// export interface BillsStatistics {
//   total_bills: number;
//   by_status: {
//     "0": number; // pending
//     "1": number; // scheduled
//     "10": number; // Error
//     "2": number; // Dealyed
//   };
//   by_review_status: Record<BillReviewStatus, number>;
//   processing_errors: number;
//   average_processing_time: number;
//   success_rate: number;
//   last_updated: string;
// }

// Update fetchBills function to use the correct parameters
export const fetchBills = async (
  params: BillsParams = {},
): Promise<BillsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  // Add parameters if they exist
  if (params.limit !== undefined) {
    // Validate limit (max 100)
    const limit = Math.min(params.limit, 100);
    queryParams.append("limit", limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.status) {
    queryParams.append("status", params.status.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  const url = `${API_BASE_URL}/api/bills${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();

    // Parse bill_json for each bill
    if (data.success && data.data && data.data.bills) {
      data.data.bills = data.data.bills.map((bill: any) => ({
        ...bill,
        bill_json: parseBillJson(bill.bill_json),
      }));
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Helper function to convert string numbers to actual numbers recursively
// Note: This function is kept for potential future use with bill data processing
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const convertStringNumbers = (obj: any, parentKey?: string): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    // Preserve these specific fields as strings (do not convert to numbers)
    const stringFieldsToPreserve = [
      "tax_id",
      "account_id",
      "original_account_id",
      "hsn_or_sac",
    ];

    if (parentKey && stringFieldsToPreserve.includes(parentKey)) {
      return obj; // Keep as string for these specific fields
    }

    // Check if string is a valid number
    const num = Number(obj);
    if (!isNaN(num) && isFinite(num) && obj.trim() !== "") {
      return num;
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item, index) => convertStringNumbers(item, parentKey));
  }

  if (typeof obj === "object") {
    const converted: any = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertStringNumbers(value, key);
    }
    return converted;
  }

  return obj;
};

export const parseBillJson = (
  billJsonString: string | null,
): BillData | null => {
  if (!billJsonString) {
    return null;
  }

  try {
    let parsedData: any;

    // Handle both string and already parsed JSON
    if (typeof billJsonString === "string") {
      parsedData = JSON.parse(billJsonString);
    } else {
      parsedData = billJsonString;
    }

    // Convert string numbers to actual numbers

    if (
      parsedData &&
      parsedData.line_items &&
      Array.isArray(parsedData.line_items)
    ) {
      parsedData.line_items = parsedData.line_items.map((item: any) => ({
        ...item,
        // Only set original_account_id if it doesn't exist, preserve existing values
        original_account_id: item.account_id,
      }));
    }

    return parsedData as BillData;
  } catch (error) {
    console.error("Error parsing bill_json:", error);
    return null;
  }
};

// Utility function to stringify bill_json from object to string
export const stringifyBillJson = (billData: BillData | null): string | null => {
  if (!billData) {
    return null;
  }

  try {
    return JSON.stringify(billData);
  } catch (error) {
    console.error("Error stringifying bill_json:", error);
    return null;
  }
};

// Get a single bill by ID
export const getBill = async (billId: string): Promise<Bill> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();

    // Parse bill_json for the single bill
    if (data.success && data.data) {
      return {
        ...data.data,
        bill_json: parseBillJson(data.data.bill_json),
      };
    }

    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update bill JSON data specifically
export const updateBillJson = async (
  billId: string,
  billJson: BillData,
): Promise<Bill> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bills/update-bill-json`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        bill_id: parseInt(billId),
        bill_json: billJson,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get bills statistics

// Update bill status
export const updateBillStatus = async (documentId: string, status: number) => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/bills/update-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: parseInt(documentId),
        status: status,
      }),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to update bill status: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return {
      ...data,
      data: {
        ...data.data,
        journals: normalizeJournals(data.data?.journals),
      },
    };
  } catch (error) {
    console.error("Error updating bill status:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Process bill JSON for GL classification
export const processBillJson = async (billData: any) => {
  try {
    const response = await fetch(
      `${CORE_API_URL}/gl_classification_interface.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          action: "process_bill_json",
          bill_json: JSON.stringify(billData),
        }),
      },
    );

    if (response.ok) {
      return {
        success: true,
      };
    }
  } catch (error) {
    console.error("Error processing bill JSON:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Create Zoho bill
export const createZohoBill = async (
  documentId: string,
  billData: any,
  pdfUrl: string,
) => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    // Get Zoho refresh token from user_data
    const companyData = getStoredCompanyData();

    // const zohoRefreshToken = localStorage.getItem("zoho_refresh_token");
    // if (!zohoRefreshToken) {
    //   throw new APIError(
    //     "No Zoho refresh token found in user data",
    //     HTTP_STATUS.UNAUTHORIZED
    //   );
    // }

    const response = await fetch(`${API_BASE_URL}/api/bills/post-to-accounting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        bill_id: parseInt(documentId),
        // zoho_refresh_token: zohoRefreshToken,
        company_gst_enabled: companyData.companyGstEnabled,
        billJson: billData,
        pdfUrl: pdfUrl,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to create Zoho invoice: ${response.statusText}`,
        response.status,
      );
    }

    // Return success without parsing response as requested
    return {
      success: true,
    };
  } catch (error) {
    console.error("Error creating Zoho bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update Zoho bill
export const updateZohoBill = async (
  documentId: string,
  billData: any,
  pdfUrl: string,
) => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    // Get company data
    const companyData = getStoredCompanyData();

    const response = await fetch(`${API_BASE_URL}/api/bills/update-in-accounting`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        bill_id: parseInt(documentId),
        company_gst_enabled: companyData.companyGstEnabled,
        billJson: billData,
        pdfUrl: pdfUrl,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to update Zoho bill: ${response.statusText}`,
        response.status,
      );
    }

    // Return success without parsing response as requested
    return {
      success: true,
    };
  } catch (error) {
    console.error("Error updating Zoho bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Mark bill as duplicate
const markBillAsDuplicate = async (billId: number): Promise<any> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/bills/mark-as-duplicate`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: billId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message ||
          `Failed to mark bill as duplicate: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return {
      ...data,
      data: {
        ...data.data,
        journals: normalizeJournals(data.data?.journals),
      },
    };
  } catch (error) {
    console.error("Error marking bill as duplicate:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Reject bill
const rejectBill = async (billId: number): Promise<any> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/bills/reject-bill`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: billId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `Failed to reject bill: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error rejecting bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Delete posted bill (from Zoho and soft-delete locally)
const deletePostedBill = async (
  billId: number,
): Promise<{
  success: boolean;
  message: string;
  data?: { bill_id: number };
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to delete bill: ${response.statusText}`,
      response.status,
    );
  }
  return data;
};

export const billsAPI = {
  fetchBills,
  getBill,
  updateBillJson,
  updateBillStatus,
  processBillJson,
  createZohoBill,
  updateZohoBill,
  markBillAsDuplicate,
  rejectBill,
  deletePostedBill,
};

export {
  getStoredToken,
  getStoredUserData,
  getStoredCompanyData,
  storeRefreshToken,
  removeToken,
  APIError,
  API_STATUS,
  HTTP_STATUS,
};

// Data invoices interfaces
export interface DataInvoice {
  id: string;
  jobid: string;
  filename: string;
  original_filename: string;
  s3_url: string;
  pushed_to_bills: string;
  is_invoice?: string;
  stage_completed?: string;
  textract_status: "failed" | "completed" | "in_progress" | "pending";
  created_at: string;
  updated_at: string;
  status: "uploaded" | "failed" | "completed";
  document_type: string;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
  order_by: string;
  order_direction: string;
}

export interface DataInvoicesResponse {
  success: boolean;
  data: DataInvoice[];
  pagination: PaginationInfo;
}

// Fetch pending data invoices with pagination
export const fetchPendingInvoices = async (
  limit: number = 5,
  offset: number = 0,
): Promise<DataInvoicesResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/data/documents?state=pending&type=invoice_processing&page=${Math.floor(offset / limit) + 1}&limit=${limit}&sort_by=created_at&sort_direction=DESC`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch completed data invoices with pagination
export const fetchCompletedInvoices = async (
  limit: number = 5,
  offset: number = 0,
): Promise<DataInvoicesResponse> => {
  console.log(
    "API: fetchCompletedInvoices called with limit:",
    limit,
    "offset:",
    offset,
  );
  const token = getStoredToken();

  if (!token) {
    console.error("API: No authentication token found");
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const url = `${API_BASE_URL}/api/data/documents?state=processed&type=invoice_processing&page=${Math.floor(offset / limit) + 1}&limit=${limit}&sort_by=created_at&sort_direction=DESC`;
    console.log("API: Fetching completed invoices from URL:", url);
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Legacy function - kept for backward compatibility
export const fetchDataInvoices = async (): Promise<DataInvoicesResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/data/invoices`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Add to imports

// Add this function to your billsAPI
export const validateDocument = async (
  billData: BillData,
  billDocument: Bill,
): Promise<ValidationResponse> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }
    const companyProfile = getStoredCompanyData();
    // Get the vendors gst from the end point /api/bills/vendors/id
    var vendor_gst = "";
    var source_of_supply = "";
    if (billData.vendor_id) {
      const vendor = await getVendorById(billData.vendor_id);
      vendor_gst = vendor.data.gstin ?? "";
      source_of_supply = vendor.data.state;
    }

    const requestBody = {
      bill_data: {
        seller_gst: billData.vendor_gstin,
        buyer_gst: billData.customer_gstin,
        company_gst: companyProfile.gst_number,
        // ZOUR-65: which of our GST registrations this document is filed
        // against. cockpit-core prefers this over `company_gst` — it resolves
        // the registration's own GSTIN and state from it. `company_gst` stays
        // for now so nothing that still reads it breaks; on a
        // single-registration tenant the two agree anyway.
        location_id: billData.location_id ?? null,
        // ZOUR-65 R3: the pipeline's own match, when the document carries one.
        // cockpit-core recomputes it from the GSTIN and supply state when this
        // is absent, so the flags work either way — but passing it through keeps
        // the pipeline's confidence and its reason text, which is what the
        // "please confirm" message on screen actually says.
        location_match: billData.location_match ?? null,
        // use the vendor Id from the bills_json and get the gst of that vendor from the vendor table.
        vendor_gst: vendor_gst,
        source_of_supply: source_of_supply,
        destination_of_supply: billData.place_of_supply,
        vendor_id: billData.vendor_id,
        gst_type: billDocument.gst_type,
        line_items: billData.line_items.map((item) => ({
          ...item,
          tax_amount: (item.item_total * (item.tax_percentage || 0)) / 100,
        })),
        total: billData.total,
        bill_number: billData.bill_number,
      },
      document_type: "bill",
      job_id: billDocument.jobid,
    };

    const response = await fetch(`${CORE_API_URL}/api_bill_validation.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      // body: JSON.stringify(billData),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to validate document: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error validating document:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const validateInvoiceDocument = async (
  invoiceData: InvoiceData,
  invoiceDocument: Invoice,
): Promise<ValidationResponse> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }
    const companyProfile = getStoredCompanyData();
    const source_of_supply = companyProfile.state;
    // Get the vendors gst from the end point /api/bills/vendors/id
    var customer_gst = "";
    var destination_of_supply = "";
    if (invoiceData.customer_id) {
      const customer = await getCustomerById(invoiceData.customer_id);
      customer_gst = customer.data.gstin_vat_tax_id || "";
      destination_of_supply = customer.data.state || "";
    }

    const requestBody = {
      bill_data: {
        seller_gst: invoiceData.vendor_gstin,
        buyer_gst: invoiceData.customer_gstin,
        company_gst: companyProfile.gst_number,
        // ZOUR-65: which of our GST registrations this document is filed
        // against. cockpit-core prefers this over `company_gst` — it resolves
        // the registration's own GSTIN and state from it. `company_gst` stays
        // for now so nothing that still reads it breaks; on a
        // single-registration tenant the two agree anyway.
        location_id: invoiceData.location_id ?? null,
        // ZOUR-65 R3: the pipeline's own match, when the document carries one.
        // cockpit-core recomputes it from the GSTIN and supply state when this
        // is absent, so the flags work either way — but passing it through keeps
        // the pipeline's confidence and its reason text, which is what the
        // "please confirm" message on screen actually says.
        location_match: invoiceData.location_match ?? null,
        // use the vendor Id from the bills_json and get the gst of that vendor from the vendor table.
        vendor_gst: customer_gst,
        // source_of_supply: invoiceData.place_of_supply,
        source_of_supply: source_of_supply,
        destination_of_supply: destination_of_supply,
        vendor_id: invoiceData.customer_id,
        gst_type: invoiceDocument.gst_type,
        line_items: invoiceData.line_items.map((item) => ({
          ...item,
          tax_amount: (item.item_total * (item.tax_percentage || 0)) / 100,
        })),
        total: invoiceData.total,
        bill_number: invoiceData.invoice_number,
      },
      document_type: "invoice",
      job_id: invoiceDocument.jobid,
    };

    const response = await fetch(`${CORE_API_URL}/api_bill_validation.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Authorization: `Bearer ${token}`,
      },
      // body: JSON.stringify(billData),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to validate document: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error validating document:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Bank Account interfaces
export interface BankAccount {
  id: string;
  bank_name: string;
  zoho_bankaccountid: string;
  currency_id: string;
  account_type: string;
  is_active: boolean;
}

export interface BankAccountsResponse {
  success: boolean;
  message: string;
  data: {
    bank_accounts: BankAccount[];
    total_count: number;
    statistics: {
      total_accounts: number;
      active_accounts: number;
      primary_accounts: number;
      paypal_accounts: number;
      bank_accounts: number;
      cash_accounts: number;
    };
  };
}

// Tax interfaces
export interface Tax {
  tax_id: string;
  tax_name: string;
  tax_percentage: string;
  tax_type: string;
  tax_specific_type: string;
  tax_authority_id: string | null;
  tax_authority_name: string | null;
  output_tax_account_name: string | null;
  tax_account_id: string | null;
  is_inactive: string;
  is_default_tax: string;
  is_editable: string;
  tax_specification: string;
  diff_rate_reason: string;
  start_date: string | null;
  end_date: string | null;
  last_modified_time: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface TaxSpecificationResponse {
  success: boolean;
  data: {
    taxes: Tax[];
    specification: string;
    pagination: {
      limit: number;
      offset: number;
    };
  };
}

// GST Determination interfaces
export interface GSTDeterminationResponse {
  success: boolean;
  tax_type: string;
  gst_type: string;
  supply_type: string;
  details: {
    source_state: string;
    destination_state: string;
    source_state_normalized: string;
    destination_state_normalized: string;
    is_inter_state: boolean;
    validation_note: string;
  };
  timestamp: string;
  request_id: string;
}

// GST Type Determination API
export const fetchGSTTypeDetermination = async (
  billData: BillData | InvoiceData,
): Promise<GSTDeterminationResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${CORE_API_URL}/api_gst_type_determination.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bill_data: billData,
        }),
      },
    );

    if (!response.ok) {
      throw new APIError(
        "Failed to determine GST type",
        response.status,
        response.statusText,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Tax specification API
export const fetchTaxSpecification = async (
  specification: string = "intra",
): Promise<TaxSpecificationResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/tax/specification/${specification}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new APIError(
        "Failed to fetch tax specification",
        response.status,
        response.statusText,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Statement upload API
export const uploadStatement = async (
  file: File,
  source: "bank" | "credit",
  bankName: string,
  zohoBankaccountid: string,
): Promise<{
  success: boolean;
  message: string;
  data: {
    id: number;
    job_id: string;
    statement_type: string;
    status: string;
    filename: string;
    file_size: number;
  };
}> => {
  const token = getStoredToken();
  const userData = getStoredUserData();

  const zohoRefreshToken = userData?.zoho_refresh_token;

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  // Validate file type (only PDF allowed)
  if (!file.type.includes("pdf")) {
    throw new APIError(
      "Only PDF files are allowed for statement uploads",
      HTTP_STATUS.BAD_REQUEST,
    );
  }

  // Create FormData
  const formData = new FormData();
  formData.append("file", file);
  formData.append("source", source);
  formData.append("bank_name", bankName);
  formData.append("zoho_bankaccountid", zohoBankaccountid);
  formData.append("zoho_refresh_token", zohoRefreshToken);

  try {
    const response = await fetch(`${API_BASE_URL}/api/data/statementUpload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type for FormData - let browser set it with boundary
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Statement Types and Interfaces
export interface Statement {
  id: number;
  job_id: string;
  filename: string;
  s3_url: string;
  folder_id: string;
  status: string;
  textract_status: string;
  pushed_to_statements: string;
  statement_type: "bank" | "credit";
  created_at: string;
  updated_at: string;
}

export interface StatementPagination {
  total: number;
  limit: number;
  offset: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
  order_by: string;
  order_direction: string;
}

export interface StatementsResponse {
  success: boolean;
  data: Statement[];
  pagination: StatementPagination;
}

// Fetch pending statements with pagination
export const fetchPendingStatements = async (
  limit: number = 5,
  offset: number = 0,
): Promise<StatementsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const url = `${API_BASE_URL}/api/data/documents?state=pending&type=statement&page=${Math.floor(offset / limit) + 1}&limit=${limit}&sort_by=created_at&sort_direction=DESC`;
    console.log("API: Fetching pending statements from URL:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch completed statements with pagination
export const fetchCompletedStatements = async (
  limit: number = 5,
  offset: number = 0,
): Promise<StatementsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const url = `${API_BASE_URL}/api/data/documents?state=processed&type=statement&page=${Math.floor(offset / limit) + 1}&limit=${limit}&sort_by=created_at&sort_direction=DESC`;
    console.log("API: Fetching completed statements from URL:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Unified interface for pending documents (invoices + statements)
export interface PendingDocument {
  id: string | number;
  jobid?: string;
  original_filename?: string;
  filename?: string;
  s3_url: string;
  document_type?: string;
  statement_type?: string;
  status: string;
  textract_status: string;
  pushed_to_bills?: string;
  stage_completed?: string;
  pushed_to_statements?: string;
  created_at: string;
  is_invoice?: string;
  updated_at: string;
  type: "invoice" | "statement";
}

// Unified interface for completed documents (invoices + statements)
export interface CompletedDocument {
  id: string | number;
  jobid?: string;
  original_filename?: string;
  filename?: string;
  s3_url: string;
  document_type?: string;
  statement_type?: string;
  status: string;
  textract_status: string;
  pushed_to_bills?: string;
  pushed_to_statements?: string;
  is_invoice?: string;
  created_at: string;
  updated_at: string;
  type: "invoice" | "statement";
}

export interface PendingDocumentsResponse {
  success: boolean;
  data: PendingDocument[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    current_page: number;
    total_pages: number;
    has_more: boolean;
    order_by: string;
    order_direction: string;
  };
}

export interface CompletedDocumentsResponse {
  success: boolean;
  data: CompletedDocument[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    current_page: number;
    total_pages: number;
    has_more: boolean;
    order_by: string;
    order_direction: string;
  };
}

// Fetch all pending documents (invoices + statements)
export const fetchPendingDocuments = async (
  limit: number = 5,
  offset: number = 0,
): Promise<PendingDocumentsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Fetch both pending invoices and statements in parallel
    limit = limit / 2;
    const [invoicesResponse, statementsResponse] = await Promise.all([
      dataInvoicesAPI.fetchPendingInvoices(limit, offset),
      fetchPendingStatements(limit, offset),
    ]);

    // Transform statements to match the unified interface
    const transformedStatements: PendingDocument[] =
      statementsResponse.data.map((statement) => ({
        id: statement.id,
        jobid: statement.job_id,
        filename: statement.filename,
        original_filename: statement.filename,
        s3_url: statement.s3_url,
        document_type: statement.statement_type,
        statement_type: statement.statement_type,
        status: statement.status,
        textract_status: statement.textract_status,
        pushed_to_statements: statement.pushed_to_statements,
        created_at: statement.created_at,
        updated_at: statement.updated_at,
        type: "statement" as const,
      }));

    // Transform invoices to match the unified interface
    const transformedInvoices: PendingDocument[] = invoicesResponse.data.map(
      (invoice) => ({
        id: invoice.id,
        jobid: invoice.jobid,
        original_filename: invoice.original_filename,
        filename: invoice.original_filename,
        s3_url: invoice.s3_url,
        document_type: invoice.document_type,
        status: invoice.status,
        textract_status: invoice.textract_status,
        stage_completed: invoice.stage_completed,
        pushed_to_bills: invoice.pushed_to_bills,
        created_at: invoice.created_at,
        updated_at: invoice.updated_at,
        type: "invoice" as const,
      }),
    );

    // Combine and sort by created_at
    const allDocuments = [...transformedInvoices, ...transformedStatements]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .slice(0, limit); // Ensure we don't exceed the limit

    // Calculate combined pagination info
    const totalItems =
      invoicesResponse.pagination.total + statementsResponse.pagination.total;

    return {
      success: true,
      data: allDocuments,
      pagination: {
        total: totalItems,
        limit,
        offset,
        current_page: Math.floor(offset / limit) + 1,
        total_pages: Math.ceil(totalItems / limit),
        has_more: offset + limit < totalItems,
        order_by: "created_at",
        order_direction: "DESC",
      },
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch all completed documents (invoices + statements)
export const fetchCompletedDocuments = async (
  limit: number = 5,
  offset: number = 0,
): Promise<CompletedDocumentsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Fetch both completed invoices and statements in parallel
    // limit = limit / 2;
    const [invoicesResponse, statementsResponse] = await Promise.all([
      dataInvoicesAPI.fetchCompletedInvoices(limit, offset),
      fetchCompletedStatements(limit, offset),
    ]);

    // Transform statements to match the unified interface
    const transformedStatements: CompletedDocument[] =
      statementsResponse.data.map((statement) => ({
        id: statement.id,
        jobid: statement.job_id,
        filename: statement.filename,
        original_filename: statement.filename,
        s3_url: statement.s3_url,
        document_type: statement.statement_type,
        statement_type: statement.statement_type,
        status: statement.status,
        textract_status: statement.textract_status,
        pushed_to_statements: statement.pushed_to_statements,
        created_at: statement.created_at,
        updated_at: statement.updated_at,
        type: "statement" as const,
      }));

    // Transform invoices to match the unified interface
    const transformedInvoices: CompletedDocument[] = invoicesResponse.data.map(
      (invoice) => ({
        id: invoice.id,
        jobid: invoice.jobid,
        original_filename: invoice.original_filename,
        filename: invoice.original_filename,
        s3_url: invoice.s3_url,
        document_type: invoice.document_type,
        status: invoice.status,
        is_invoice: invoice.is_invoice,
        textract_status: invoice.textract_status,
        pushed_to_bills: invoice.pushed_to_bills,
        created_at: invoice.created_at,
        updated_at: invoice.updated_at,
        type: "invoice" as const,
      }),
    );

    // Combine and sort by created_at
    const allDocuments = [...transformedInvoices, ...transformedStatements]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .slice(0, limit); // Ensure we don't exceed the limit

    // Calculate combined pagination info
    const totalItems =
      invoicesResponse.pagination.total + statementsResponse.pagination.total;

    return {
      success: true,
      data: allDocuments,
      pagination: {
        total: totalItems,
        limit,
        offset,
        current_page: Math.floor(offset / limit) + 1,
        total_pages: Math.ceil(totalItems / limit),
        has_more: offset + limit < totalItems,
        order_by: "created_at",
        order_direction: "DESC",
      },
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const dataInvoicesAPI = {
  fetchDataInvoices,
  fetchPendingInvoices,
  fetchCompletedInvoices,
  uploadStatement,
  fetchPendingStatements,
  fetchCompletedStatements,
  fetchPendingDocuments,
  fetchCompletedDocuments,
};

// Invoice Types and Interfaces
export type InvoiceStatus = "0" | "1" | "2" | "3" | "4" | "10";

export type InvoiceReviewStatus = "0" | "1" | "2";

// Invoice Line Item interface
export interface InvoiceLineItem {
  name: string;
  quantity: number;
  rate: number;
  amount: number;
}

// Invoice Data interface based on the JSON structure
export interface InvoiceData {
  /**
   * ZOUR-65 — the pipeline's location match for this document
   * (`{confidence, reason, input_gstin, location_id, name, is_active}`).
   * Absent on a document the pipeline has not touched; core recomputes it then.
   */
  location_match?: Record<string, unknown> | null;
  /**
   * ZOUR-65 — the Zoho location (GST registration) this document is filed
   * against. null/absent means the primary location.
   */
  location_id?: string | null;
  customer_id: string;
  invoice_number: string;
  discount?: number;
  date: string; // ISO date string
  due_date: string; // ISO date string
  reference_number: string;
  currency_id: string;
  currency_code: CurrencyCode;
  currency_symbol: string;
  exchange_rate: number;
  vendor_name: string;
  vendor_gstin: string;
  gst_no: string;
  gst_treatment: GstTreatment;
  customer_name: string;
  customer_gstin: string;
  source_of_supply: string;
  destination_of_supply: string;
  place_of_supply: string;
  invoice_date: string; // ISO date string
  eway_bill_number: string;
  irn_number: string;
  line_items: LineItem[];
  sub_total: number;
  tax_total: number;
  total: number;
  payment_terms: number;
  payment_terms_label: string;
  is_item_level_tax_calc: boolean;
  is_inclusive_tax: boolean;
  notes: string;
  terms: string;
  // Additional fields that might be present
  processing_metadata?: {
    extracted_at: string;
    confidence_score: number;
    extraction_method: string;
    version: string;
  };
  validation_results?: {
    is_valid: boolean;
    errors: string[];
    warnings: string[];
  };
  customer_address?: string;
}

// Main Invoice interface
export interface Invoice {
  // Core Identifiers
  id: number;
  jobid: string;
  companyid: number;

  // File Information
  filename: string;
  original_filename: string;
  file_size: number;
  folder_id: string;

  // Processed Data
  invoice_json: InvoiceData;
  invoice_candidates: any | null;
  customer_vector: any | null;
  gst_rates: string;
  gst_type: string;

  // Status Information
  reviewed: InvoiceReviewStatus;
  status: InvoiceStatus;

  // External Integration
  zoho_invoiceid: string | null;

  // System Fields
  deleted: number;
  created_at: string;
  updated_at: string;

  // File Access
  signed_url: string;
  signed_url_expires_at: string;

  // Manual documents: attachments with signed URLs
  attachment_urls?: Array<{
    signed_url: string;
    filename: string;
    file_size?: number | null;
    expires_in?: number;
  }>;
}

// Invoice Pagination interface
export interface InvoicePagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Invoice Response interface
export interface InvoiceResponse {
  success: boolean;
  data: {
    invoices: Invoice[];
    pagination: InvoicePagination;
  };
  message?: string;
}

// Invoice Error Response interface
export interface InvoiceErrorResponse {
  success: false;
  error: string;
  message?: string;
  details?: {
    field?: string;
    code?: string;
    value?: any;
  };
}

export type InvoiceApiResponse = InvoiceResponse | InvoiceErrorResponse;

// Invoice Parameters interface (same as BillsParams for consistency)
export interface InvoiceParams {
  // Pagination
  limit?: number;
  offset?: number;

  // Filtering
  status?: InvoiceStatus;
  reviewed?: InvoiceReviewStatus;
  search?: string;
  companyid?: number;

  // Date Filtering
  date_from?: string;
  date_to?: string;
  created_after?: string;
  created_before?: string;

  // Sorting
  sort_by?:
    | "created_at"
    | "updated_at"
    | "filename"
    | "status"
    | "original_filename";
  sort_order?: "asc" | "desc";

  // Additional Filters
  has_zoho_invoiceid?: boolean;
  has_errors?: boolean;
  min_confidence?: number;
  max_confidence?: number;
}

// Helper function to parse invoice JSON
export const parseInvoiceJson = (
  invoiceJsonString: string | null,
): InvoiceData | null => {
  if (!invoiceJsonString) {
    return null;
  }

  try {
    let parsedData: any;

    // Handle both string and already parsed JSON
    if (typeof invoiceJsonString === "string") {
      parsedData = JSON.parse(invoiceJsonString);
    } else {
      parsedData = invoiceJsonString;
    }

    return parsedData as InvoiceData;
  } catch (error) {
    console.error("Error parsing invoice_json:", error);
    return null;
  }
};

// Fetch invoices function
export const fetchInvoices = async (
  params: InvoiceParams = {},
): Promise<InvoiceResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  // Add parameters if they exist
  if (params.limit !== undefined) {
    // Validate limit (max 100)
    const limit = Math.min(params.limit, 100);
    queryParams.append("limit", limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.status) {
    queryParams.append("status", params.status.toString());
  }

  if (params.reviewed) {
    queryParams.append("reviewed", params.reviewed.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  if (params.companyid) {
    queryParams.append("companyid", params.companyid.toString());
  }

  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }

  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }

  if (params.created_after) {
    queryParams.append("created_after", params.created_after);
  }

  if (params.created_before) {
    queryParams.append("created_before", params.created_before);
  }

  if (params.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }

  if (params.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }

  const url = `${API_BASE_URL}/api/invoices${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();

    // Parse invoice_json for each invoice
    if (data.success && data.data && data.data.invoices) {
      data.data.invoices = data.data.invoices.map((invoice: any) => ({
        ...invoice,
        invoice_json: parseInvoiceJson(invoice.invoice_json),
      }));
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get a single invoice by ID
export const getInvoice = async (invoiceId: number): Promise<Invoice> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update invoice JSON data specifically
export const updateInvoiceJson = async (
  invoiceId: number,
  invoiceJson: InvoiceData,
): Promise<Invoice> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/update-invoice-json`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          invoice_json: invoiceJson,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update invoice status
export const updateInvoiceStatus = async (
  invoiceId: number,
  status: number,
) => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/invoices/update-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: invoiceId,
        status: status,
      }),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to update invoice status: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Create Zoho invoice
export const createZohoInvoice = async (
  invoiceData: any,
  documentId: string,
  pdfUrl: string,
) => {
  try {
    const token = getStoredToken();
    const companyData = getStoredCompanyData();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    // Get Zoho refresh token from user_data
    const userData = getStoredUserData();

    const zohoRefreshToken = userData?.zoho_refresh_token;
    if (!zohoRefreshToken) {
      throw new APIError(
        "No Zoho refresh token found in user data",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/post-to-accounting`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_id: parseInt(documentId),
          zoho_refresh_token: zohoRefreshToken,
          invoiceJson: invoiceData,
          company_gst_enabled: companyData.companyGstEnabled,
          pdfUrl: pdfUrl,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to create Zoho invoice: ${response.statusText}`,
        response.status,
      );
    }

    // Return success without parsing response as requested
    return {
      success: true,
    };
  } catch (error) {
    console.error("Error creating Zoho invoice:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Mark invoice as duplicate
export const markInvoiceAsDuplicate = async (
  invoiceId: number,
): Promise<any> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/mark-as-duplicate`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: invoiceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message ||
          `Failed to mark invoice as duplicate: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error marking invoice as duplicate:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Reject invoice
export const rejectInvoice = async (invoiceId: number): Promise<any> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/reject-invoice`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: invoiceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `Failed to reject invoice: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error rejecting invoice:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Delete posted invoice (from Zoho and soft-delete locally)
export const deletePostedInvoice = async (
  invoiceId: number,
): Promise<{
  success: boolean;
  message: string;
  data?: { invoice_id: number };
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to delete invoice: ${response.statusText}`,
      response.status,
    );
  }
  return data;
};

// Currency Types and Interfaces
export interface Currency {
  id: number;
  currency_id: string;
  currency_code: string;
  currency_name: string;
  currency_name_formatted: string;
  currency_symbol: string;
  price_precision: number;
  currency_format: string;
  is_base_currency: number;
  exchange_rate: string;
  effective_date: string | null;
  auto_exchange_rate_enabled: number;
  created_at: string;
  updated_at: string;
}

export interface CurrenciesResponse {
  success: boolean;
  data: Currency[];
  total: number;
}

// Fetch currencies
export const fetchCurrencies = async (): Promise<CurrenciesResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/currencies`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch bank accounts
export const fetchBankAccounts = async (): Promise<BankAccountsResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cashAndBank/bank-accounts`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Credit Card Account Types
export interface CreditCardAccount {
  id: number;
  bank_name: string;
  zoho_bankaccountid: string;
  currency_id: string;
  account_type: string;
  is_active: boolean;
  is_auto: boolean;
  balance: number;
  bank_balance: number;
  is_primary_account: boolean;
  statement_suggestion_count: number;
  is_paypal_account: boolean;
  currency_code: string;
  created_at: string;
  updated_at: string;
}

export interface CreditCardAccountsResponse {
  success: boolean;
  message: string;
  data: {
    credit_card_accounts: CreditCardAccount[];
    total_count: number;
    statistics: {
      total_credit_cards: number;
      active_credit_cards: number;
      inactive_credit_cards: number;
    };
  };
}

// Fetch credit card accounts
export const fetchCreditCardAccounts =
  async (): Promise<CreditCardAccountsResponse> => {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cashAndBank/credit-card-accounts`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData,
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );
    }
  };

// All Accounts Types (Bank + Credit Card)
export interface AllAccount {
  id: number;
  bank_name: string;
  zoho_bankaccountid: string;
  account_number?: string | null;
  currency_id: string;
  account_type: "bank" | "credit" | "credit_card";
  is_active: boolean;
  is_auto: boolean;
  balance: number;
  bank_balance: number;
  is_primary_account: boolean;
  statement_suggestion_count: number;
  is_paypal_account: boolean;
  currency_code: string;
  opening_balance?: number | null;
  opening_balance_date?: string | null;
  opening_balance_id?: string | null;
  pending_review?: {
    id: number;
    status: "pending" | "approved" | "rejected";
    action_code: string;
    created_at: string;
  } | null;
  created_at: string;
  updated_at: string;
  pending_customer_payments: {
    count: number;
    total_amount: number;
  };
  pending_vendor_payments: {
    count: number;
    total_amount: number;
  };
}

export interface AllAccountsResponse {
  success: boolean;
  message: string;
  data: {
    accounts: AllAccount[];
    total_count: number;
    statistics: {
      total_accounts: number;
      by_account_type: {
        bank: {
          count: number;
          active: number;
          inactive: number;
        };
        credit: {
          count: number;
          active: number;
          inactive: number;
        };
      };
      active_accounts: number;
      inactive_accounts: number;
    };
  };
}

/**
 * Create a bank account in Zoho Books and insert into bank_accounts table.
 * POST /api/bank-accounts/create
 */
export const createBankAccount = async (payload: {
  account_name: string;
  account_type: "bank" | "credit";
  account_number: string;
  currency_id: string;
  currency_code: string;
  opening_balance?: number;
  opening_balance_date?: string;
}): Promise<{
  id: number;
  zoho_bankaccountid: string;
  tally_ledger_name?: string;
  tally_ledger_guid?: string;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bank-accounts/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data,
      );
    }

    if (!data.success || !data.data) {
      throw new APIError(
        data.message || "Failed to create bank account",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
        data,
      );
    }

    return {
      id: data.data.id,
      zoho_bankaccountid: data.data.zoho_bankaccountid,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const updateBankAccount = async (
  id: number,
  payload: {
    account_name: string;
    account_type: "bank" | "credit";
    account_number: string;
    currency_id: string;
    currency_code: string;
    opening_balance?: number;
    opening_balance_date?: string;
  },
): Promise<{ id: number; zoho_bankaccountid: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bank-accounts/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data,
      );
    }

    if (!data.success || !data.data) {
      throw new APIError(
        data.message || "Failed to update bank account",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
        data,
      );
    }

    return {
      id: data.data.id,
      zoho_bankaccountid: data.data.zoho_bankaccountid,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const deleteBankAccount = async (id: number): Promise<void> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bank-accounts/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data,
      );
    }

    if (!data.success) {
      throw new APIError(
        data.message || "Failed to delete bank account",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
        data,
      );
    }
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch all accounts (bank + credit card)
export const getAllAccounts = async (): Promise<AllAccountsResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cashAndBank/all-accounts`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Uncategorize a reconciled transaction
export const uncategorizeTransaction = async (
  transactionId: string,
  fromTransactionId?: string,
  toTransactionId?: string,
): Promise<any> => {
  const body: any = {
    transaction_id: transactionId,
  };

  if (fromTransactionId) {
    body.from_transaction_id = fromTransactionId;
  }
  if (toTransactionId) {
    body.to_transaction_id = toTransactionId;
  }

  return makeRequest("/api/bank-transactions/uncategorize", {
    method: "POST",
    body: JSON.stringify(body),
  });
};

// Fetch activity logs for a specific transaction
export const fetchTransactionActivityLogs = async (
  transactionId: string,
): Promise<TransactionActivityLogsResponse> => {
  return makeRequest(`/api/bank-transactions/${transactionId}/activity-logs`, {
    method: "GET",
  });
};

// Fetch categorized transaction details for view mode
export const fetchTransactionViewDetails = async (
  transactionId: string,
): Promise<CustomerTransactionViewDetailsResponse> => {
  return makeRequest(`/api/bank-transactions/${transactionId}/view-details`, {
    method: "GET",
  });
};

// Explicit helper for customer transaction detail fetches (alias for clarity)
export const fetchCustomerTransactionViewDetails = async (
  transactionId: string,
): Promise<CustomerTransactionViewDetailsResponse> => {
  return fetchTransactionViewDetails(transactionId);
};

export const currenciesAPI = {
  fetchCurrencies,
};

// Process bill JSON for GL classification
export const processInvoiceJson = async (invoiceData: any) => {
  try {
    const response = await fetch(
      `${CORE_API_URL}/gl_classification_interface.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "process_invoice_json",
          invoice_json: invoiceData,
        }),
      },
    );

    if (response.ok) {
      return {
        success: true,
      };
    }
  } catch (error) {
    console.error("Error processing bill JSON:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const invoicesAPI = {
  fetchInvoices,
  getInvoice,
  updateInvoiceJson,
  updateInvoiceStatus,
  createZohoInvoice,
  markInvoiceAsDuplicate,
  rejectInvoice,
  deletePostedInvoice,
  processInvoiceJson,
};

// Journal interfaces
export interface JournalLineItem {
  id: number;
  account_id: string;
  account_name: string;
  contact_id?: string | null;
  contact_name?: string | null;
  description: string;
  amount: number;
  is_debit_or_credit: "debit" | "credit";
  // Legacy fields for backward compatibility
  zoho_lineid?: string | null;
  journal_id?: string;
  customer_or_vendor?: "customer" | "vendor" | null;
  created_at?: string;
  updated_at?: string;
  account_code?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
}

export interface JournalAttachment {
  index: number;
  signed_url: string;
  filename: string;
  mime_type: string;
  size: number;
  expires_in?: number;
}

// Template Line Item interface
export interface TemplateLineItem {
  id: number;
  template_id?: number;
  account_id: string;
  account_name: string;
  account_code?: string;
  account_type?: string;
  contact_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  customer_or_vendor?: "customer" | "vendor" | null;
  description: string;
  amount: number;
  is_debit_or_credit: "debit" | "credit";
  created_at?: string;
  updated_at?: string;
}

// Generated Journal from Template
export interface GeneratedJournal {
  id: number;
  date: string;
  reference_number: string;
  journal_number: string;
  status: string;
  created_at: string;
  total_debit: string;
  total_credit: string;
}

// Template interface for recurring journals
export interface JournalTemplate {
  id: number;
  user_id?: number;
  template_name: string;
  type?: string;
  currency_id?: string;
  currency_symbol?: string;
  /**
   * ZOUR-65 F4: the GST registration every journal this template mints is filed
   * under. NULL means the primary location.
   */
  location_id?: string | null;
  notes?: string;
  reference_number_prefix?: string;
  recurrence_frequency: string;
  recurrence_interval: number | null;
  starts_on: string;
  ends_on: string | null;
  never_expires: string | number | boolean;
  is_paused: string | boolean;
  is_cancelled: string | boolean;
  paused_at: string | null;
  pause_reason?: string | null;
  resumed_at?: string | null;
  cancelled_at: string | null;
  cancel_reason?: string | null;
  last_processed_date?: string | null;
  total_generated_count?: number;
  created_at?: string;
  updated_at?: string;
  next_posting_date?: string;
  line_items?: TemplateLineItem[];
  generated_journals?: GeneratedJournal[];
  pending_review?: {
    id: number;
    status: "pending" | "approved" | "rejected";
    action_code: string;
    created_at: string;
  } | null;
}

// Template Response with pagination
export interface JournalTemplatesResponse {
  success: boolean;
  data: {
    templates: JournalTemplate[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  };
  message: string;
}

// Single Template Detail Response
export interface JournalTemplateDetailResponse {
  success: boolean;
  data: JournalTemplate;
  message: string;
}

// Related journal entry

export interface Journal {
  id: number;
  zoho_journalid: string | null;
  tally_remote_id?: string | null;
  tally_voucher_number?: string | null;
  tally_master_id?: number | string | null;
  tally_guid?: string | null;
  posted_to_tally_date?: string | null;
  /**
   * ZOUR-65: the GST registration this journal is filed under (Zoho's
   * `location_id`). NULL means the primary location — an absent value is the
   * default answer, not a missing one.
   */
  location_id?: string | null;
  date: string;
  journal_number: string;
  reference_number: string | null;
  notes: string;
  status: "approved" | "rejected" | "under_review";
  type: string;
  currency_id: number;
  currency_symbol: string;
  template_id: number | null;
  is_generated_from_template: boolean;
  total_debit?: number;
  total_credit?: number;
  created_at?: string;
  updated_at?: string;
  approved_at?: string | null;
  // For recurring journals - these fields will be present
  template_name?: string;
  recurrence_frequency?:
    | "start_of_month"
    | "end_of_month"
    | "daily"
    | "every_n_days";
  recurrence_interval?: number | null;
  // For detail page
  line_items?: JournalLineItem[];
  attachment_urls?: JournalAttachment[];
  template?: JournalTemplate;
  journal_type?: string;
}

const toBooleanFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1";

const normalizeJournal = (journal: Journal): Journal => ({
  ...journal,
  is_generated_from_template: toBooleanFlag(
    (journal as { is_generated_from_template?: unknown })
      .is_generated_from_template,
  ),
});

const normalizeJournals = (journals: Journal[] | undefined | null): Journal[] =>
  (journals ?? []).map(normalizeJournal);
export interface ParentJournal {
  id: string;
  journal_number: string;
  date: string;
  recurring_journal_name: string | null;
  recurrence_frequency: string | null;
  recurrence_interval: string | null;
  starts_on: string | null;
  ends_on: string | null;
  never_expires: string | null;
}

export interface JournalsResponse {
  success: boolean;
  message: string;
  data: {
    journals: Journal[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  };
}

// Fetch all journals with pagination
const fetchJournals = async (
  limit: number = 10,
  offset: number = 0,
): Promise<JournalsResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals?limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return {
      ...data,
      data: {
        ...data.data,
        journals: normalizeJournals(data.data?.journals),
      },
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Create draft journal
export interface CreateJournalLineItem {
  account_id: string;
  is_debit_or_credit: "debit" | "credit";
  amount: number;
  description: string;
  contact_id?: string;
  customer_or_vendor?: "customer" | "vendor";
}

export interface CreateJournalRequest {
  journal: {
    date: string;
    // journal_number: string;
    currency_id: string;
    currency_symbol: string;
    notes: string;
    type: string;
  };
  line_items: CreateJournalLineItem[];
}

const createDraftJournal = async (
  data: CreateJournalRequest,
  attachments: File[] = [],
  status: "save_as_draft" | "under_review" = "save_as_draft",
): Promise<{ success: boolean; message: string; data: Journal }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    let requestBody: BodyInit;
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    // Add status to the data
    const requestData = {
      ...data,
      status: status,
    };

    // If there are attachments, send as FormData
    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(requestData));

      // Append each file
      attachments.forEach((file, index) => {
        formData.append(`attachments[]`, file);
      });

      requestBody = formData;
      // Don't set Content-Type for FormData, browser will set it with boundary
    } else {
      // Send as JSON if no attachments
      headers["Content-Type"] = "application/json";
      requestBody = JSON.stringify(requestData);
    }

    const response = await fetch(`${API_BASE_URL}/api/journals/draft`, {
      method: "POST",
      headers,
      body: requestBody,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get single journal by ID
const getJournalById = async (
  journalId: string,
): Promise<{ success: boolean; message: string; data: Journal }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/journals/${journalId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return {
      ...data,
      data: data.data ? normalizeJournal(data.data) : data.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update journal
const updateJournal = async (
  journalId: string,
  data: CreateJournalRequest,
  attachments: File[] = [],
): Promise<{ success: boolean; message: string; data: Journal }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    let requestBody: BodyInit;
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    // If there are attachments, send as FormData
    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      // Append each file
      attachments.forEach((file, index) => {
        formData.append(`attachments[]`, file);
      });

      requestBody = formData;

      // Don't set Content-Type for FormData, browser will set it with boundary
    } else {
      // Send as JSON if no attachments
      headers["Content-Type"] = "application/json";
      requestBody = JSON.stringify(data);
    }

    const response = await fetch(`${API_BASE_URL}/api/journals/draft`, {
      method: "POST",
      headers,
      body: requestBody,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get account types
export interface AccountTypesResponse {
  success: boolean;
  message: string;
  data: {
    account_types: string[];
    count: number;
    has_more?: boolean;
    total?: number;
  };
}

const getAccountTypes = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
  journal_type?: "manual" | "ap_ar";
}): Promise<AccountTypesResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    if (params?.journal_type)
      queryParams.append("journal_type", params.journal_type);

    const url = `${API_BASE_URL}/api/gl-maps/account-types${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get GL maps by account type
export interface GLMap {
  id?: string | number;
  gl_id: string;
  gl_name: string;
  account_id: string;
  is_active: number;
  account_type: string;
  parent_id?: string | null;
  parent_name?: string | null;
  is_under_review?: number | string;
  created_at: string;
  updated_at: string;
  bill?: number;
  invoice?: number;
}

export interface GLMapsByAccountTypeResponse {
  success: boolean;
  message: string;
  data: {
    account_type: string;
    gl_maps: GLMap[];
    count: number;
    has_more?: boolean;
    total?: number;
  };
}

const getGLMapsByAccountType = async (
  accountType: string,
  params?: {
    search?: string;
    limit?: number;
    offset?: number;
  },
): Promise<GLMapsByAccountTypeResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());

    const url = `${API_BASE_URL}/api/gl-maps/by-account-type/${encodeURIComponent(
      accountType,
    )}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export interface GLMapsListResponse {
  success: boolean;
  message: string;
  data: {
    gl_maps: GLMap[];
    count: number;
    has_more?: boolean;
    total?: number;
    account_type?: string;
  };
}

/**
 * Get GL maps list with optional account type and journal_type filter (uses /list endpoint).
 * When account_type is omitted, returns all accounts (paginated) unless journal_type is set:
 * - journal_type=manual: exclude accounts_payable and accounts_receivable
 * - journal_type=ap_ar: only AP/AR account types (accounts_payable, accounts_receivable, other_current_asset, other_current_liability)
 */
export const getGLMaps = async (params?: {
  account_type?: string;
  journal_type?: "manual" | "ap_ar";
  search?: string;
  limit?: number;
  offset?: number;
  management?: boolean;
}): Promise<GLMapsListResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const queryParams = new URLSearchParams();
    if (params?.account_type)
      queryParams.append("account_type", params.account_type);
    if (params?.journal_type)
      queryParams.append("journal_type", params.journal_type);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit != null)
      queryParams.append("limit", params.limit.toString());
    if (params?.offset != null)
      queryParams.append("offset", params.offset.toString());
    if (params?.management) queryParams.append("management", "1");

    const url = `${API_BASE_URL}/api/gl-maps/list${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Update bill and invoice flags for a GL mapping.
 * POST /api/gl-maps/:glId/bill-invoice
 */
export const updateGlMapBillInvoice = async (
  glId: string | number,
  payload: { bill?: boolean; invoice?: boolean },
): Promise<{ success: boolean; message: string; data: { gl_map: GLMap } }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/${encodeURIComponent(String(glId))}/bill-invoice`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to update GL mapping: ${response.statusText}`,
      response.status,
      data,
    );
  }
  return data;
};

/**
 * Get GL mapping by account ID
 */
const getGLMapByAccountId = async (
  accountId: string | number,
): Promise<{
  success: boolean;
  message: string;
  data: GLMap;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const url = `${API_BASE_URL}/api/gl-maps/by-account-id/${accountId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Failed to fetch GL mapping: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get GL maps for credits or refund received (income, expense, fixed_asset only).
 * GET /api/gl-maps/credits-or-refund-received-accounts
 */
const getCreditsOrRefundReceivedAccounts = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<GLMapsListResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  try {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit != null)
      queryParams.append("limit", params.limit.toString());
    if (params?.offset != null)
      queryParams.append("offset", params.offset.toString());
    const url = `${API_BASE_URL}/api/gl-maps/credits-or-refund-received-accounts${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }
    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Post journal to accounting backend (create new and post)
const postJournalToAccounting = async (
  data: CreateJournalRequest,
  attachments: File[] = [],
): Promise<{
  success: boolean;
  posted_to_accounting?: boolean;
  posted_to_zoho?: boolean;
  message: string;
  data: Journal;
  error_details?: string;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    let requestBody: BodyInit;
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    // If there are attachments, send as FormData
    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      // Append each file
      attachments.forEach((file) => {
        formData.append(`attachments[]`, file);
      });

      requestBody = formData;
      // Don't set Content-Type for FormData, browser will set it with boundary
    } else {
      // Send as JSON if no attachments
      headers["Content-Type"] = "application/json";
      requestBody = JSON.stringify(data);
    }

    const response = await fetch(`${API_BASE_URL}/api/journals/post-to-accounting`, {
      method: "POST",
      headers,
      body: requestBody,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Post existing journal to accounting backend
const postExistingJournalToAccounting = async (
  journalId: string,
  data: CreateJournalRequest,
  attachments: File[] = [],
): Promise<{
  success: boolean;
  posted_to_accounting?: boolean;
  posted_to_zoho?: boolean;
  message: string;
  data: Journal;
  error_details?: string;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    let requestBody: BodyInit;
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    // If there are attachments, send as FormData
    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      // Append each file
      attachments.forEach((file) => {
        formData.append(`attachments[]`, file);
      });

      requestBody = formData;
    } else {
      headers["Content-Type"] = "application/json";
      requestBody = JSON.stringify(data);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/journals/post-to-accounting/${journalId}`,
      {
        method: "POST",
        headers,
        body: requestBody,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Reject journal
const rejectJournal = async (
  journalId: string,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/reject`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Delete journal
const deleteJournal = async (
  journalId: string,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/journals/${journalId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// AP/AR Journal: get by id (returns shape compatible with Journal for detail page)
const getApArJournalById = async (
  id: number,
): Promise<{ success: boolean; data: Journal }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/ap-ar-journals/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to fetch AP/AR journal",
      response.status,
      data,
    );
  }
  const raw = data.data;
  const journal: Journal = {
    id: raw.id,
    zoho_journalid: null,
    date: raw.date,
    journal_number: raw.reference_number || "",
    reference_number: raw.reference_number || null,
    notes: raw.notes || "",
    currency_id:
      typeof raw.currency_id === "number"
        ? raw.currency_id
        : Number(raw.currency_id) || 0,
    currency_symbol: raw.currency_symbol || "₹",
    type: raw.type || "both",
    status: (raw.status || "approved") as Journal["status"],
    template_id: null,
    is_generated_from_template: false,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    line_items: (raw.line_items || []).map((li: any, idx: number) => ({
      id: li.id ?? idx,
      account_id: li.account_id,
      account_type: li.account_type ?? null,
      account_name: li.account_name ?? li.account_type ?? li.account_id,
      contact_id: li.contact_id ?? null,
      contact_name: li.contact_name ?? null,
      customer_or_vendor: li.customer_or_vendor ?? null,
      description: li.description || "",
      amount: Number(li.amount),
      is_debit_or_credit: li.is_debit_or_credit,
      bill_id: li.bill_id ?? null,
      invoice_id: li.invoice_id ?? null,
    })),
  };
  return { success: true, data: journal };
};

// AP/AR Journal: delete
const deleteApArJournal = async (
  id: number,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(
    `${API_BASE_URL}/api/ap-ar-journals/${id}/delete`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to delete AP/AR journal",
      response.status,
      data,
    );
  }
  return data;
};

// AP/AR Journal: create (for New Journal Entry when AP/AR mode)
export interface CreateApArJournalPayload {
  journal: {
    date: string;
    reference_number?: string;
    notes?: string;
    currency_id: string;
    currency_symbol?: string;
    type?: "cash" | "both";
  };
  line_items: Array<{
    account_id: string;
    contact_id?: string | null;
    customer_or_vendor?: "customer" | "vendor" | null;
    description?: string | null;
    amount: number;
    is_debit_or_credit: "debit" | "credit";
    invoice_id?: string | null;
    bill_id?: string | null;
    /** Status of selected invoice/bill (e.g. overdue, open) for credit summary cache */
    document_status?: string | null;
    /** Balance of selected invoice/bill at selection time for credit summary cache */
    document_balance?: number | null;
  }>;
}

const createApArJournal = async (
  payload: CreateApArJournalPayload,
): Promise<{ success: boolean; message: string; data: { id: number } }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/ap-ar-journals`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to create AP/AR journal",
      response.status,
      data,
    );
  }
  return data;
};

const createApArJournalDraft = async (
  payload: CreateApArJournalPayload,
): Promise<{ success: boolean; message: string; data: { id: number } }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/ap-ar-journals/draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to create AP/AR journal draft",
      response.status,
      data,
    );
  }
  return data;
};

const finalizeApArJournal = async (
  id: number,
): Promise<{ success: boolean; message: string; data: { id: number } }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(
    `${API_BASE_URL}/api/ap-ar-journals/${id}/finalize`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to finalize AP/AR journal",
      response.status,
      data,
    );
  }
  return data;
};

// Make journal recurring
export interface MakeRecurringRequest {
  template_name: string;
  recurrence_frequency: string;
  recurrence_interval?: number | null;
  reference_number_prefix?: string;
  starts_on: string;
  ends_on?: string | null;
  never_expires: boolean;
}

export interface MakeRecurringResponse {
  success: boolean;
  data: Journal;
  message?: string;
}

// Pause recurring journal
export interface PauseRecurringJournalRequest {
  pause_reason: string;
}

export interface PauseRecurringJournalResponse {
  success: boolean;
  message?: string;
}

// Resume recurring journal
export interface ResumeRecurringJournalResponse {
  success: boolean;
  message?: string;
}

const makeJournalRecurring = async (
  journalId: string,
  data: MakeRecurringRequest,
): Promise<MakeRecurringResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/make-recurring`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

const pauseRecurringJournal = async (
  journalId: string,
  pauseReason: string,
): Promise<PauseRecurringJournalResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/pause`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pause_reason: pauseReason }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

const resumeRecurringJournal = async (
  journalId: string,
): Promise<ResumeRecurringJournalResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/resume`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch all journal templates with pagination
const fetchJournalTemplates = async (
  limit: number = 10,
  offset: number = 0,
): Promise<JournalTemplatesResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates?limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const responseData = await response.json();
    return {
      ...responseData,
      data: responseData.data
        ? normalizeJournal(responseData.data)
        : responseData.data,
    };
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Fetch single journal template by ID
const getJournalTemplateById = async (
  templateId: string,
): Promise<JournalTemplateDetailResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates/${templateId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Pause recurring journal template
const pauseRecurringTemplate = async (
  templateId: string,
  pauseReason: string,
  pauseDuration?: string,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates/${templateId}/pause`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pause_reason: pauseReason,
          pause_duration_months: parseInt(pauseDuration || "1"),
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Resume recurring journal template
const resumeRecurringTemplate = async (
  templateId: string,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates/${templateId}/resume`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Cancel recurring journal template
const cancelRecurringTemplate = async (
  templateId: string,
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates/${templateId}/cancel`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update recurring journal template
export interface UpdateTemplateRequest {
  template_name?: string;
  type?: string;
  notes?: string;
  /**
   * ZOUR-65 F4: the GST registration future journals from this template are
   * filed under. NULL means the primary location.
   */
  location_id?: string | null;
  recurrence_frequency?: string;
  recurrence_interval?: number | null;
  starts_on?: string;
  ends_on?: string | null;
  never_expires?: boolean;
  line_items?: CreateJournalLineItem[];
}

const updateRecurringTemplate = async (
  templateId: string,
  templateData: UpdateTemplateRequest,
): Promise<{ success: boolean; message: string; data?: any }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/templates/${templateId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(templateData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Duplicate journal
const duplicateJournal = async (
  journalId: number,
): Promise<{
  success: boolean;
  message: string;
  data: { new_journal_id: number; original_journal_id: number };
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/duplicate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const journalsAPI = {
  fetchJournals,
  fetchJournalTemplates,
  getJournalTemplateById,
  pauseRecurringTemplate,
  resumeRecurringTemplate,
  cancelRecurringTemplate,
  updateRecurringTemplate,
  getJournalById,
  getApArJournalById,
  deleteApArJournal,
  createApArJournal,
  createApArJournalDraft,
  finalizeApArJournal,
  createDraftJournal,
  updateJournal,
  postJournalToAccounting,
  postExistingJournalToAccounting,
  getAccountTypes,
  getGLMapsByAccountType,
  getGLMaps,
  getGLMapByAccountId,
  getCreditsOrRefundReceivedAccounts,
  rejectJournal,
  deleteJournal,
  makeJournalRecurring,
  pauseRecurringJournal,
  resumeRecurringJournal,
  duplicateJournal,
};

// Activity Logs API
export interface ActivityLog {
  id: number;
  activity_name: string;
  action_type: string;
  user_id: number;
  description: string;
  for_feature: string;
  for_feature_id: number;
  changes: any;
  ip_address: string;
  user_agent: string;
  created_at: string;
  user_email: string;
  user_name: string;
  user_role: string;
}

export interface ActivityLogsResponse {
  success: boolean;
  data: {
    logs: ActivityLog[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  };
  message: string;
}

const fetchActivityLogs = async (
  feature: string,
  featureId: string | number,
  limit: number = 50,
  offset: number = 0,
): Promise<ActivityLogsResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/activity-logs/feature/${feature}/${featureId}?limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const activityLogsAPI = {
  fetchActivityLogs,
};

// TDS Rate interfaces
export interface TDSRate {
  id: number;
  tds_tax_id: string;
  tds_tax_name: string;
  tds_tax_percentage: number;
  tds_tax_type: string;
  business_display: boolean;
  individual_display: boolean;
  is_expired: boolean;
  section: string;
  created_at: string;
  updated_at: string;
  tax_specific_type?: string | null;
  llm_context?: string | null;
  /** Curated locally. 0 means "no threshold — always deduct" (e.g. section 195). */
  threshold?: number;
}

export interface TdsCurationPayload {
  section?: string;
  business_display?: boolean;
  individual_display?: boolean;
  llm_context?: string;
  /** Sent as a string so a cleared box reaches the backend as "" (→ 0), not undefined. */
  threshold?: string;
}

/**
 * Update the curated (local-only) fields of a TDS rate.
 * POST /api/get-tds/curation/{id}
 */
export const updateTdsCuration = async (
  id: number,
  payload: TdsCurationPayload,
): Promise<{ success: boolean; message: string; data: { tds_rate: TDSRate } }> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(`${API_BASE_URL}/api/get-tds/curation/${id}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

export interface TdsSuggestPayload {
  id?: number;
  draft: string;
  tax_name?: string;
  tax_percentage?: string;
  tax_specific_type?: string;
  section?: string;
}

/**
 * Ask the AI to write/rewrite an llm_context description for a TDS rate.
 * Pass an existing rate's id, or ad-hoc metadata (tax_name/percentage/section)
 * for a rate not created yet. Returns suggestion text only — nothing is saved.
 * POST /api/get-tds/llm-context/suggest
 */
export const suggestTdsLlmContext = async (
  payload: TdsSuggestPayload,
): Promise<{ success: boolean; message: string; data: { suggestion: string } }> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(`${API_BASE_URL}/api/get-tds/llm-context/suggest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

export interface TDSRatesResponse {
  success: boolean;
  message: string;
  data: {
    tds_rates: TDSRate[];
    total_count: number;
    statistics: {
      total_rates: number;
      business_rates: number;
      individual_rates: number;
      unique_tax_types: number;
      tax_types: string[];
    };
    filters_applied: {
      business_display: boolean | null;
      individual_display: boolean | null;
      tax_type: string | null;
    };
  };
}

// Fetch TDS rates
export const fetchTDSRates = async (): Promise<TDSRatesResponse> => {
  const url = `${API_BASE_URL}/api/get-tds`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: TDSRatesResponse = await response.json();
  return data;
};

// Check if transaction exists in another account
export interface CheckTransactionExistsResponse {
  data?: {
    success: boolean;
    exists: boolean;
    transaction_id?: string;
    data?: {
      amount: string;
      currency_symbol: string;
      date: string;
      account_name: string;
      [key: string]: any;
    };
  };
}

export const checkTransactionExists = async (
  accountId: string,
  date: string,
  amount: number,
  debitOrCredit: string,
): Promise<CheckTransactionExistsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/check-exists?account_id=${accountId}&date=${date}&amount=${amount}&debitOrCredit=${debitOrCredit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize customer transfer
export interface CategorizeCustomerTransferRequest {
  transaction_id: string;
  from_transaction_id: string;
  date: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  category_type: string;
  reference_number: string;
}

export interface CategorizeCustomerTransferResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
  from_transaction_id?: string;
}

export const categorizeCustomerTransfer = async (
  requestData: CategorizeCustomerTransferRequest,
): Promise<CategorizeCustomerTransferResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-customer-transfer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize interest income
export interface CategorizeInterestIncomeRequest {
  transaction_id: string;
  to_account_id: string;
  /** Required for Tally (income GL). Zoho ignores this — it auto-picks interest. */
  from_account_id?: string;
  amount: number;
  date: string;
  payment_mode: string;
  reference_number?: string;
  category_type?: string;
}

export interface CategorizeInterestIncomeResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeInterestIncome = async (
  requestData: CategorizeInterestIncomeRequest,
): Promise<CategorizeInterestIncomeResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-interest-income`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Deposit from other accounts interfaces
export interface DepositGLMap {
  gl_id: number;
  gl_name: string;
  account_id: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface DepositAccountTypeGroup {
  account_type: string;
  account_type_formatted: string;
  count: number;
  gl_maps: DepositGLMap[];
}

export interface DepositFromOtherAccountsResponse {
  success: boolean;
  data: {
    gl_maps_by_type: DepositAccountTypeGroup[];
    total_count: number;
    account_types_count: number;
    account_types_included: string[];
    has_more?: boolean;
    total?: number;
  };
  message: string;
}

export const fetchDepositFromOtherAccounts = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
  bank_account_id?: string;
}): Promise<DepositFromOtherAccountsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    if (params?.bank_account_id) {
      queryParams.append("bank_account_id", params.bank_account_id);
    }

    const url = `${API_BASE_URL}/api/gl-maps/customer-deposit-from-other-accounts${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const fetchTransferToOtherAccounts = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
  bank_account_id?: string;
}): Promise<DepositFromOtherAccountsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    if (params?.bank_account_id) {
      queryParams.append("bank_account_id", params.bank_account_id);
    }

    const url = `${API_BASE_URL}/api/gl-maps/vendor-transfer-to-other-accounts${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize other income
export interface CategorizeOtherIncomeRequest {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface CategorizeOtherIncomeResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeOtherIncome = async (
  requestData: CategorizeOtherIncomeRequest,
): Promise<CategorizeOtherIncomeResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-other-income`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize customer deposit from other accounts
export interface CategorizeCustomerDepositRequest {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  reference_number: string;
  payment_mode: string;
  customer_id?: string;
}

export interface CategorizeCustomerDepositResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeCustomerDeposit = async (
  requestData: CategorizeCustomerDepositRequest,
): Promise<CategorizeCustomerDepositResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-customer-deposit-from-other-accounts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize customer deposit split from other accounts
export interface CategorizeCustomerDepositSplitRequest {
  transaction_id: string;
  date: string;
  reference_number: string;
  customer_id?: string;
  splits: Array<{
    from_account_id: string;
    amount: number;
    payment_mode: string;
  }>;
}

export interface CategorizeCustomerDepositSplitResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeCustomerDepositSplit = async (
  requestData: CategorizeCustomerDepositSplitRequest,
): Promise<CategorizeCustomerDepositSplitResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-customer-deposit-from-other-accounts-split`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize customer owners contribution
export interface CategorizeOwnersContributionRequest {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface CategorizeOwnersContributionResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeOwnersContribution = async (
  requestData: CategorizeOwnersContributionRequest,
): Promise<CategorizeOwnersContributionResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-customer-owners-contribution`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize vendor transfer
export interface CategorizeVendorTransferRequest {
  transaction_id: string;
  to_transaction_id: string;
  date: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  category_type: string;
  reference_number: string;
}

export interface CategorizeVendorTransferResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeVendorTransfer = async (
  requestData: CategorizeVendorTransferRequest,
): Promise<CategorizeVendorTransferResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-transfer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize vendor transfer to other accounts
export interface CategorizeVendorTransferToOtherAccountsRequest {
  transaction_id: string;
  date: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  payment_mode: string;
  reference_number?: string;
}

export interface CategorizeVendorTransferToOtherAccountsResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeVendorTransferToOtherAccounts = async (
  requestData: CategorizeVendorTransferToOtherAccountsRequest,
): Promise<CategorizeVendorTransferToOtherAccountsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-transfer-to-other-accounts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize vendor transfer to other accounts (split)
export interface CategorizeVendorTransferToOtherAccountsSplitRequest {
  transaction_id: string;
  from_account_id: string;
  date: string;
  reference_number: string;
  splits: Array<{
    to_account_id: string;
    amount: number;
    payment_mode: string;
  }>;
}

export interface CategorizeVendorTransferToOtherAccountsSplitResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
  split_transactions?: number;
}

export interface VendorTransferToOtherAccountsSplitCategoryDetails {
  from_account_id?: string | null;
  from_account_name?: string;
  date?: string;
  reference_number?: string;
  splits?: Array<{
    to_account_id: string;
    to_account_name: string;
    amount: number;
    payment_mode: string;
  }>;
  total_amount?: number;
  split_count?: number;
}

export const categorizeVendorTransferToOtherAccountsSplit = async (
  requestData: CategorizeVendorTransferToOtherAccountsSplitRequest,
): Promise<CategorizeVendorTransferToOtherAccountsSplitResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-transfer-to-other-accounts-split`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize vendor owners drawings
export interface CategorizeOwnerDrawingRequest {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  payment_mode: string;
  reference_number?: string;
}

export interface CategorizeOwnerDrawingResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeOwnerDrawing = async (
  requestData: CategorizeOwnerDrawingRequest,
): Promise<CategorizeOwnerDrawingResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-owners-drawings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Vendor Payment interfaces
export interface VendorPayment {
  payment_id: string;
  vendor_id: string;
  vendor_name: string;
  bill_numbers: string;
  payment_mode: string;
  payment_number: string;
  description: string;
  date: string;
  reference_number: string;
  exchange_rate: number;
  amount: number;
  bcy_amount: number;
  paid_through_account_id: string;
  paid_through_account_name: string;
  balance: number;
  bcy_balance: number;
  created_time: string;
  last_modified_time: string;
  is_paid_via_print_check: boolean;
  has_attachment: boolean;
  is_ach_payment: boolean;
  ach_payment_status: string;
  ach_gw_transaction_id: string;
  check_details: any[];
}

export interface VendorPaymentsResponse {
  success: boolean;
  data: {
    vendor_id: string;
    payments: VendorPayment[];
    total_payments: number;
    total_payments_fetched: number;
  };
  message: string;
}

// Fetch vendor payments
export const fetchVendorPayments = async (
  vendorId: string,
  transactionDate?: string,
): Promise<VendorPaymentsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const url = transactionDate
      ? `${API_BASE_URL}/api/bills/vendor-payments?vendor_id=${vendorId}&transaction_date=${transactionDate}`
      : `${API_BASE_URL}/api/bills/vendor-payments?vendor_id=${vendorId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize vendor payment refund
export interface CategorizeVendorPaymentRefundRequest {
  transaction_id: string;
  vendor_id: string;
  vendor_name?: string;
  payment: {
    payment_id: string;
    payment_number: string;
    amount: number;
  };
  date: string;
  payment_mode: string;
  reference_number: string;
  to_account_id: string;
}

export interface CategorizeVendorPaymentRefundResponse {
  success: boolean;
  data: {
    message: string;
    transaction_id: string;
    successful_payments: Array<{
      vendorpayment_id: string;
      amount: number;
    }>;
    failed_payments: any[];
    success_count: number;
    failed_count: number;
    total_count: number;
  };
  message: string;
}

export const categorizeVendorPaymentRefund = async (
  requestData: CategorizeVendorPaymentRefundRequest,
): Promise<CategorizeVendorPaymentRefundResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-payment-refund`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export interface CategorizeCreditsOrRefundReceivedRequest {
  transaction_id: string;
  date: string;
  payment_mode: string;
  from_account_id: string;
  amount: number;
  reference_number?: string;
  description?: string;
  vendor_id?: string | null;
  vendor_name?: string | null;
}

export interface CategorizeCreditsOrRefundReceivedResponse {
  success: boolean;
  data?: { message: string; transaction_id: string };
  message: string;
}

export const categorizeCreditsOrRefundReceived = async (
  requestData: CategorizeCreditsOrRefundReceivedRequest,
): Promise<CategorizeCreditsOrRefundReceivedResponse> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-credits-or-refund-received`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }
    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Customer Payment interfaces
export interface CustomerPayment {
  payment_id: string;
  payment_number: string;
  invoice_number: string;
  date: string;
  payment_mode: string;
  amount: number;
  unused_amount: number;
  bcy_amount: number; // This is the balance amount
  location_id: string;
  location_name: string;
}

export interface CustomerPaymentsResponse {
  success: boolean;
  data: {
    customer_id: string;
    payments: CustomerPayment[];
    total_payments: number;
    total_payments_fetched: number;
  };
  message: string;
}

// Fetch customer payments
export const fetchCustomerPayments = async (
  customerId: string,
): Promise<CustomerPaymentsResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/customer-payments?customer_id=${customerId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Categorize customer payment refund (vendor side)
export interface CategorizeCustomerPaymentRefundRequest {
  transaction_id: string;
  customer_id: string;
  customer_name?: string;
  payment: {
    payment_id: string;
    payment_number: string;
    amount: number;
  };
  date: string;
  payment_mode: string;
  reference_number: string;
  from_account_id: string;
}

export interface CategorizeCustomerPaymentRefundResponse {
  success: boolean;
  data: {
    message: string;
    transaction_id: string;
    successful_payments: Array<{
      customerpayment_id: string;
      amount: number;
    }>;
    failed_payments: any[];
    success_count: number;
    failed_count: number;
    total_count: number;
  };
  message: string;
}

export const categorizeCustomerPaymentRefund = async (
  requestData: CategorizeCustomerPaymentRefundRequest,
): Promise<CategorizeCustomerPaymentRefundResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-customer-payment-refund`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const categorizeCustomerExpenseRefund = async (requestBody: {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
}): Promise<{ success: boolean; message: string }> => {
  return makeRequest(
    "/api/bank-transactions/categorize-customer-expense-refund",
    {
      method: "POST",
      body: JSON.stringify(requestBody),
    },
  );
};

// Categorize vendor card payment
export interface CategorizeVendorCardPaymentRequest {
  transaction_id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  reference_number?: string;
}

export interface CategorizeVendorCardPaymentResponse {
  success: boolean;
  message: string;
  transaction_id?: string;
}

export const categorizeVendorCardPayment = async (
  requestData: CategorizeVendorCardPaymentRequest,
): Promise<CategorizeVendorCardPaymentResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bank-transactions/categorize-vendor-card-payment`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Customer Groups Types and Interfaces
export interface CustomerGroup {
  group_id: number;
  group_name: string;
  group_description?: string;
  customer_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CustomerGroupsResponse {
  success: boolean;
  message: string;
  data: CustomerGroup[];
}

// Fetch customer groups
export const fetchCustomerGroups =
  async (): Promise<CustomerGroupsResponse> => {
    const token = getStoredToken();

    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/invoices/customer-groups`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData,
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );
    }
  };

// Create customer group
export const createCustomerGroup = async (groupData: {
  group_name: string;
  group_description?: string;
}): Promise<{
  success: boolean;
  message: string;
  data: CustomerGroup;
}> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    console.log(`${API_BASE_URL}/api/invoices/customer-groups`);
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/customer-groups`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(groupData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Semantic Overrides API
export interface SemanticOverrideRequest {
  line_item: {
    vendor: string;
    vendor_id: string;
    description: string;
    product_description: string;
    hsn: string;
    hsn_description: string;
    customer: string;
    customer_id: string;
  };
  gl_id: string;
  similarity_threshold: number;
  document_type: string;
  account_id: string;
  note: string;
}

export interface SemanticOverrideResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const createSemanticOverride = async (
  overrideData: SemanticOverrideRequest,
): Promise<SemanticOverrideResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${CORE_API_URL}/api_semantic_overrides.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": "sk-semantic-overrides-2024-abc123",
      },
      body: JSON.stringify(overrideData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Signed URL Types
export interface SignedUrlRequest {
  documentId: number | string;
  documentType: "bill" | "invoice" | "debit_note" | "credit_note";
  expiration?: number; // Optional: defaults to 3600 (1 hour), max 604800 (7 days)
}

export interface SignedUrlData {
  documentId: number;
  documentType: string;
  signedUrl: string;
  expiresAt: string;
  expiresIn: number;
  filename: string;
  s3Bucket: string;
  s3Key: string;
}

export interface SignedUrlResponse {
  success: boolean;
  message: string;
  data: SignedUrlData;
}

// Get signed URL for document
export const getSignedUrlForDocument = async (
  requestData: SignedUrlRequest,
): Promise<SignedUrlResponse> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/get-signed-url-for-doc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// ==================== SYNC API ====================

export interface SyncResult {
  success: boolean;
  message: string;
  data: {
    total_from_zoho: number;
    new_added: number;
    existing: number;
    errors: number;
    error_details: string[];
  };
}

export interface ChartOfAccountsSyncResult {
  success: boolean;
  message: string;
  data: {
    total_from_zoho: number;
    processed_accounts: number;
    new_added: number;
    updated: number;
    deleted: number;
    errors: number;
    error_details: string[];
  };
}

/**
 * Sync currencies from Zoho Books API
 * POST /api/sync/currencies
 */
export const syncCurrencies = async (): Promise<SyncResult> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/sync/currencies`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Sync taxes from Zoho Books API
 * POST /api/sync/taxes
 */
export const syncTaxes = async (): Promise<SyncResult> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/sync/taxes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export interface TdsSyncFieldChange {
  old: string | number;
  new: string | number;
}

/**
 * Why a rate still needs a human after the sync seeded what it could
 * (ZOUR-55). `null` on a row the catalogue completed.
 */
export type TdsCurationReason =
  | "unknown_section"
  | "shared_section"
  | "needs_context"
  | null;

export interface TdsSyncPreviewRow {
  tds_tax_id: string;
  tds_tax_name: string;
  tds_tax_percentage: number;
  tds_tax_type: string;
  tax_specific_type: string;
  is_expired: number;
  zoho_status: string;
  local_id?: number;
  /**
   * The section as it will be STORED: the catalogue label for a new row, the
   * curated value for an existing one. Empty only when the catalogue does not
   * know the Zoho slug.
   */
  section?: string | null;
  llm_context?: string | null;
  changes?: Record<string, TdsSyncFieldChange>;
  /**
   * Blank curated columns this sync would fill, column => value. The display
   * flags arrive as 1 when the row is bare (nothing curated, hidden from both
   * pickers), so a backfilled row ends up where a freshly synced one does.
   */
  label_fill?: Record<string, string | number>;
  curation_reason?: TdsCurationReason;
}

export interface TdsSyncOrphanRow {
  local_id: number;
  tds_tax_id: string;
  tds_tax_name: string;
  tds_tax_percentage: number;
  section: string | null;
  is_expired: number;
}

export interface TdsSyncPreviewResult {
  success: boolean;
  message: string;
  data: {
    total_from_zoho: number;
    counts: {
      new: number;
      changed: number;
      same: number;
      orphaned: number;
      /** Rows Zoho has not moved but whose blank curation the sync would fill. */
      labelled: number;
    };
    new: TdsSyncPreviewRow[];
    changed: TdsSyncPreviewRow[];
    same: TdsSyncPreviewRow[];
    orphaned: TdsSyncOrphanRow[];
    labelled: TdsSyncPreviewRow[];
    needs_curation: TdsSyncPreviewRow[];
  };
}

export interface TdsSyncApplyResult {
  success: boolean;
  message: string;
  data: {
    total_from_zoho: number;
    new_added: number;
    updated: number;
    unchanged: number;
    /** Existing rows whose blank section / AI context this sync filled in. */
    labelled: number;
    needs_curation: TdsSyncPreviewRow[];
    orphaned: TdsSyncOrphanRow[];
    errors: number;
    error_details: string[];
  };
}

/**
 * Preview TDS tax sync — fetches Zoho's TDS tax list and diffs it against the
 * local tds_rates table without writing anything.
 * GET /api/sync/tds/preview
 */
export const previewTdsSync = async (): Promise<TdsSyncPreviewResult> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/sync/tds/preview`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Apply TDS tax sync — the backend re-fetches from Zoho and upserts into
 * tds_rates (curated columns are preserved; local-only rows are never deleted).
 * POST /api/sync/tds/apply
 */
export const applyTdsSync = async (): Promise<TdsSyncApplyResult> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/sync/tds/apply`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export interface TdsSectionType {
  id: number;
  act_version: number;
  tax_specific_type: string;
  description: string;
  section_no: string | null;
  old_section_no: string | null;
}

export interface TdsZohoWritePayload {
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  start_date?: string;
  end_date?: string;
  section?: string;
  business_display?: boolean;
  individual_display?: boolean;
  llm_context?: string;
  /** Local-only curation carried through the Zoho write so it survives versioning. */
  threshold?: string;
  /** Per-rate ledger override. Omitted means "let Zoho pick", which is the norm. */
  tds_payable_account_id?: string;
  /** NOTE: tax_account_id is the RECEIVABLE on a TDS rate. Zoho's naming. */
  tax_account_id?: string;
}

export interface TdsLedgerAccount {
  account_id: string;
  account_name: string;
}

export interface TdsLedgerSlot {
  field: string;
  accounts: TdsLedgerAccount[];
  /** What Zoho would use if no account is named. Null when there is no evidence. */
  default_account_id: string | null;
}

export interface TdsLedgersResult {
  success: boolean;
  data: { slots: { payable: TdsLedgerSlot; receivable: TdsLedgerSlot } };
}

/** The two slots, in the shape both rate dialogs hold them. */
export type TaxLedgerSlots = {
  payable: TdsLedgerSlot;
  receivable: TdsLedgerSlot;
};

/**
 * The rate form's "+ New account" — one account into the org's real chart of
 * accounts. Only the two types a rate can point at are accepted; `kind` decides
 * which tax's slots come back, not what kind of account is made.
 */
export interface TaxLedgerCreatePayload {
  account_name: string;
  account_type: "other_current_liability" | "other_current_asset";
  kind: "tds" | "tcs";
}

export interface TaxLedgerCreateResult {
  success: boolean;
  message: string;
  data: {
    account: TdsLedgerAccount & { account_type: string };
    /** The re-read offer, same shape as fetchTdsLedgers/fetchTcsLedgers. */
    slots: TaxLedgerSlots;
    mirror_warning: string | null;
  };
}

/**
 * One authenticated JSON call to a sync endpoint.
 *
 * The TDS and TCS helpers below are the same request with a different path, so
 * the token check and error unwrapping live here rather than nine times over.
 */
const syncRequest = async <T>(
  path: string,
  method: "GET" | "POST" | "PUT" = "GET",
  body?: unknown,
): Promise<T> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

/**
 * Accounts the TDS rate form may offer for each ledger slot.
 * GET /api/sync/tds/ledgers
 */
export const fetchTdsLedgers = (): Promise<TdsLedgersResult> =>
  syncRequest<TdsLedgersResult>("/api/sync/tds/ledgers");

/** A nature-of-collection Zoho will accept, read live from the org. */
export interface TcsSection {
  tax_specific_type: string;
  description: string;
  section_no: string;
  old_section_no: string;
}

export interface TcsRateCreatePayload {
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  start_date?: string;
  end_date?: string;
  /** NOTE: on TCS, tax_account_id is the PAYABLE. The mirror of TDS. */
  tax_account_id?: string;
  tcs_receivable_account_id?: string;
}

export interface TcsSyncPreview {
  success: boolean;
  data: {
    total_from_zoho: number;
    new: Array<{ tcs_id: string; tax_name: string; tax_rate: number }>;
    changed: Array<{
      tcs_id: string;
      tax_name: string;
      changes: Record<string, { old: string; new: string }>;
    }>;
    same: unknown[];
    local_only: unknown[];
  };
}

export interface TcsSyncApply {
  success: boolean;
  message: string;
  data: { total_from_zoho: number; created: number; updated: number };
}

/** GET /api/sync/tcs/sections — read live, TCS has no seeded catalogue. */
export const fetchTcsSections = (): Promise<{
  success: boolean;
  data: { sections: TcsSection[] };
}> => syncRequest("/api/sync/tcs/sections");

/** GET /api/sync/tcs/ledgers */
export const fetchTcsLedgers = (): Promise<TdsLedgersResult> =>
  syncRequest<TdsLedgersResult>("/api/sync/tcs/ledgers");

/** POST /api/sync/tcs/zoho — creates in the real Zoho org, mirrors locally. */
export const createTcsInZoho = (
  payload: TcsRateCreatePayload,
): Promise<{ success: boolean; message: string; data: { tcs_id: string; local_warning: string | null } }> =>
  syncRequest("/api/sync/tcs/zoho", "POST", payload);

export interface TcsZohoUpdateResult {
  success: boolean;
  message: string;
  data: {
    /** Zoho's answer, which is a NEW id when the rate was already in use. */
    tcs_id: string;
    versioned?: boolean;
    previous_tcs_id?: string | null;
    local_warning?: string | null;
  };
}

/**
 * Change a TCS rate in the real Zoho org, then mirror the result.
 *
 * Zoho's update is a FULL REPLACE, so the payload carries every field whether
 * or not it changed, end_date included when empty — that is how Zoho spells
 * open-ended. Versioning-aware: a rate already used on an invoice comes back
 * under a new tcs_id with the old one expired.
 *
 * PUT /api/sync/tcs/zoho/{tcs_id}
 */
export const updateTcsInZoho = (
  tcsId: string,
  payload: TcsRateCreatePayload,
): Promise<TcsZohoUpdateResult> =>
  syncRequest<TcsZohoUpdateResult>(`/api/sync/tcs/zoho/${tcsId}`, "PUT", payload);

/**
 * Add one account to the org's chart of accounts, from inside a rate form.
 *
 * Hands back the re-read slot offer so the dropdown that asked can swap its
 * list in place and select what it just made.
 *
 * POST /api/sync/tax-ledgers
 */
export const createTaxLedgerAccount = (
  payload: TaxLedgerCreatePayload,
): Promise<TaxLedgerCreateResult> =>
  syncRequest<TaxLedgerCreateResult>("/api/sync/tax-ledgers", "POST", payload);

/** GET /api/sync/tcs/preview */
export const previewTcsSync = (): Promise<TcsSyncPreview> =>
  syncRequest<TcsSyncPreview>("/api/sync/tcs/preview");

/** The account one rate's tax lands in, as the ledger map returns it. */
export interface TaxLedgerMapEntry {
  kind: "tds" | "tcs";
  payable_account_id: string | null;
  /** Null when Zoho would not say — the register prints "—" rather than guess. */
  payable_account_name: string | null;
}

export interface TaxLedgerMapResult {
  success: boolean;
  data: {
    /** Keyed by Zoho tax id: tds_rates.tds_tax_id / tcs.tcs_id. */
    ledgers: Record<string, TaxLedgerMapEntry>;
    cached?: boolean;
  };
}

/**
 * Which account each TDS and TCS rate's tax lands in, read live from Zoho.
 *
 * Neither `tds_rates` nor `tcs` stores an account id, deliberately: the account
 * is Zoho's answer and a copy here would go stale the moment somebody renamed
 * the ledger. So the register asks at the moment it is opened, and the column
 * prints "—" while this is in flight or if it fails. NOTHING ON THE PAGE WAITS
 * ON IT.
 *
 * The server caches its answer per organisation for a few minutes, because the
 * TDS half costs one Zoho detail call per rate — the listing does not carry the
 * payable account at all. Pass `refresh` after writing a rate, where the cached
 * answer is known to be one rate short.
 *
 * GET /api/sync/tax-ledger-map
 */
export const fetchTaxLedgerMap = (
  refresh = false,
): Promise<TaxLedgerMapResult> =>
  syncRequest<TaxLedgerMapResult>(
    `/api/sync/tax-ledger-map${refresh ? "?refresh=1" : ""}`,
  );

/** POST /api/sync/tcs/apply */
export const applyTcsSync = (): Promise<TcsSyncApply> =>
  syncRequest<TcsSyncApply>("/api/sync/tcs/apply", "POST");

/**
 * Fetch the TDS nature-of-payment catalogue for the section dropdown.
 * GET /api/sync/tds/sections
 */
export const fetchTdsSections = async (
  actVersion = 2,
): Promise<TdsSectionType[]> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/sync/tds/sections?act_version=${actVersion}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  const data = await response.json();
  return data.data.sections;
};

interface TdsZohoWriteResult {
  success: boolean;
  message: string;
  data: {
    tds_tax_id: string;
    versioned?: boolean;
    previous_tds_tax_id?: string | null;
    local_warning?: string | null;
  };
}

/**
 * Create a TDS rate in Zoho Books (then mirrored locally).
 * POST /api/sync/tds/zoho
 */
export const createTdsInZoho = async (
  payload: TdsZohoWritePayload,
): Promise<TdsZohoWriteResult> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  const response = await fetch(`${API_BASE_URL}/api/sync/tds/zoho`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

/**
 * Update a TDS rate in Zoho Books (then mirrored locally). Versioning-aware:
 * the response may carry a new tds_tax_id if Zoho expired an in-use rate.
 * PUT /api/sync/tds/zoho/{tds_tax_id}
 */
export const updateTdsInZoho = async (
  tdsTaxId: string,
  payload: TdsZohoWritePayload,
): Promise<TdsZohoWriteResult> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/sync/tds/zoho/${tdsTaxId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

/**
 * Sync chart of accounts from Zoho Books API
 * POST /api/sync/chartofaccounts
 */
export const syncChartOfAccounts =
  async (): Promise<ChartOfAccountsSyncResult> => {
    const token = getStoredToken();

    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/sync/chartofaccounts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData,
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );
    }
  };

/** What POST /api/currencies answers with. */
export interface CurrencyCreateResult {
  success: boolean;
  message: string;
  data: {
    currency_id: string;
    /**
     * True when Zoho created the currency but the local mirror failed. The
     * write is NOT retryable at that point — Zoho would refuse the duplicate —
     * so the message tells the user to run the currency sync instead.
     */
    local_warning: boolean;
  };
}

/**
 * Enable a currency: created in Zoho Books first, then mirrored locally.
 * POST /api/currencies
 *
 * Admin only, and there is no undo here. Zoho lets a currency be deleted only
 * while nothing references it.
 */
export const createCurrency = (payload: {
  currency_code: string;
  price_precision: number;
  currency_symbol?: string;
}): Promise<CurrencyCreateResult> =>
  syncRequest<CurrencyCreateResult>("/api/currencies", "POST", payload);
